import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { DS } from '../PatientForms/designTokens';
import FormLayout from './FormLayout';
import FormSubmittedView from './FormSubmittedView';
import { FormRadioGroup } from './FormRadioGroup';
import FormTextInput from './FormTextInput';
import { transformFormData } from './transformFormData';
import { toggleExclusive } from './toggleExclusive';
import { setPatientFormValue } from './setPatientFormValue';
import { useUnansweredGate } from './useUnansweredGate';
import { isValidPregnancyWeek } from './formValidation';
import { NEW_VISIT_FORM_VERSION, useFormVersionTag } from './formVersion';
import UnansweredQuestions, {
  UNANSWERED_QUESTIONS_ID,
} from './UnansweredQuestions';
import {
  UserAnswers,
  AdditionalHeadache,
  AdditionalAttackMedication,
  AdditionalPreventiveMedication,
  Medication,
  PreviousMedication,
  PreviousPreventiveMedication,
  OtherMedication,
  Allergy,
} from './types';
import { getMyNewVisit, saveMyNewVisit } from './patientFormApi';
import { useAutoSave } from './useAutoSave';
import HasAdditionalPreventiveMedicationHeadache from './formExtraSections/HasAdditionalPreventiveMedicationHeadache';
import HasAdditionalAttackMedication from './formExtraSections/HasAdditionalAttackMedication';
import HasMultipleHeadaches from './formExtraSections/HasMultipleHeadaches';
import HasAdditionalPreviousMedication from './formExtraSections/HasAdditionalPreviousMedication';
import MultiplePreviousPreventiveMedications from './formExtraSections/MultiplePreviousPreventiveMedication';
import MultipleOtherMedications from './formExtraSections/MultipleOtherMedication';
import MultipleAllergies from './formExtraSections/MultipleAllergies';
import { reportError } from '../../lib/sentry';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #D1D5DB',
  borderRadius: '6px',
  marginTop: '8px',
  fontFamily: DS.fontBody,
  fontSize: '16px',
  boxSizing: 'border-box',
};

const NewVisitForm: React.FC = () => {
  const [diagnosedConditions, setDiagnosedConditions] = useState<string[]>([]);
  const [diagnosisConditionsData, setDiagnosisConditionsData] =
    useState<string>('');
  const [
    diagnosedConditionsDateSpanningshuvudvark,
    setDiagnosedConditionsDateSpanningshuvudvark,
  ] = useState<string>('');
  const [
    diagnosisConditionsDataSpanningshuvudvark,
    setDiagnosisConditionsDataSpanningshuvudvark,
  ] = useState<string>('');
  const [diagnosedConditionsDateHorton, setDiagnosedConditionsDateHorton] =
    useState<string>('');
  const [diagnosisConditionsDataHorton, setDiagnosisConditionsDataHorton] =
    useState<string>('');
  const [updateMedicationTwo, setUpdateMedicationTwo] = useState<string>('');

  const [otherPreventiveMedicationExept, setOtherPreventiveMedicationExept] =
    useState<string>('');
  const [otherHeadacheType, setOtherHeadacheType] = useState<string>('');
  const [diagnosedConditionsDateOther, setDiagnosedConditionsDateOther] =
    useState<string>('');
  const [hasMultipleHeadaches, setHasMultipleHeadaches] = useState<string>('');
  const [hasWithdrawal, setHasWithdrawal] = useState<string>('');
  const [withdrawalDate, setWithdrawalDate] = useState<string>('');
  const [withdrawalEffect, setWithdrawalEffect] = useState<string>('');

  const [preventiveMedicationNameHeadic, setPreventiveMedicationNameHeadic] =
    useState<string>('');

  const [preventiveMedicationDoseHeadic, setPreventiveMedicationDoseHeadic] =
    useState<string>('');

  const [
    preventiveMedicationEffectivenessHeadic,
    setPreventiveMedicationEffectivenessHeadic,
  ] = useState<string>('');

  const [
    preventiveMedicationStartYearHeadic,
    setPreventiveMedicationStartYearHeadic,
  ] = useState<string>('');
  const [nonMedicationTreatments, setNonMedicationTreatments] = useState<
    string[]
  >([]);
  const [massageEffect, setMassageEffect] = useState<string>('');
  const [naprapatEffect, setNaprapatEffect] = useState<string>('');
  const [acupunctureEffect, setAcupunctureEffect] = useState<string>('');
  const [tensEffect, setTensEffect] = useState<string>('');
  const [yogaEffect, setYogaEffect] = useState<string>('');
  const [bettskenEffect, setBettskenEffect] = useState<string>('');
  const [takesPainMedication, setTakesPainMedication] = useState<string>('');
  const [painMedicationName, setPainMedicationName] = useState<string>('');
  const [painMedicationDose, setPainMedicationDose] = useState<string>('');
  const [painMedicationEffect, setPainMedicationEffect] = useState<string>('');
  const [painMedicationEffectiveness, setPainMedicationEffectiveness] =
    useState<string>('');
  const [painMedicationFrequency, setPainMedicationFrequency] =
    useState<string>('');
  const [hasAdditionalAttackMedication, setHasAdditionalAttackMedication] =
    useState<string>('');
  const [hasPreviousPainMedication, setHasPreviousPainMedication] =
    useState<string>('');
  const [previousMedicationName, setPreviousMedicationName] =
    useState<string>('');
  const [previousMedicationNameSecond, setPreviousMedicationNameSecond] =
    useState<string>('');
  const [previousMedicationDose, setPreviousMedicationDose] =
    useState<string>('');
  const [previousMedicationDoseSecond, setPreviousMedicationDoseSecond] =
    useState<string>('');
  const [previousMedicationFrequency, setPreviousMedicationFrequency] =
    useState<string>('');
  const [
    previousMedicationFrequencySecond,
    setPreviousMedicationFrequencySecond,
  ] = useState<string>('');
  const [previousMedicationStartYear, setPreviousMedicationStartYear] =
    useState<string>('');
  const [
    previousMedicationStartYearSecond,
    setPreviousMedicationStartYearSecond,
  ] = useState<string>('');
  const [previousMedicationStopReason, setPreviousMedicationStopReason] =
    useState<string>('');
  const [
    previousMedicationStopReasonSecond,
    setPreviousMedicationStopReasonSecond,
  ] = useState<string>('');
  const [previousMedicationSideEffect, setPreviousMedicationSideEffect] =
    useState<string>('');
  const [previousMedicationOtherReason, setPreviousMedicationOtherReason] =
    useState<string>('');
  const [
    previousMedicationOtherReasonTwo,
    setPreviousMedicationOtherReasonTwo,
  ] = useState<string>('');
  const [previousMedicationStopYear, setPreviousMedicationStopYear] =
    useState<string>('');
  const [
    hasOtherPreviousPainMedicationStop,
    setHasOtherPreviousPainMedicationStop,
  ] = useState<string>('');
  const [
    hasOtherPreviousPainMedicationStopSecond,
    setHasOtherPreviousPainMedicationStopSecond,
  ] = useState<string>('');
  const [medicationOveruse, setMedicationOveruse] = useState<string>('');
  const [overusedMedication, setOverusedMedication] = useState<string>('');
  const [overuseDuration, setOveruseDuration] = useState<string>('');
  const [ageFirstAttack, setAgeFirstAttack] = useState<string>('');
  const [headacheDuration, setHeadacheDuration] = useState<string>('');
  const [painLocation, setPainLocation] = useState<string>('');
  const [attackPattern, setAttackPattern] = useState<string>('');
  const [attackPatternDuring, setAttackPatternDuring] = useState<string>('');
  const [painCharacter, setPainCharacter] = useState<string>('');
  const [painIntensity, setPainIntensity] = useState<string>('');
  const [physicalActivityEffect, setPhysicalActivityEffect] =
    useState<string>('');
  const [preferredActionDuringAttack, setPreferredActionDuringAttack] =
    useState<string>('');
  const [associatedSymptoms, setAssociatedSymptoms] = useState<string[]>([]);
  const [auraSymptoms, setAuraSymptoms] = useState<string[]>([]);
  const [dizzinessType, setDizzinessType] = useState<string>('');
  const [dizzinessDuration, setDizzinessDuration] = useState<string>('');
  const [headacheFrequency, setHeadacheFrequency] = useState<string>('');
  const [frequencyDuration, setFrequencyDuration] = useState<string>('');
  const [bloodPressureValue, setBloodPressureValue] = useState<string>('');
  const [worseningOverTime, setWorseningOverTime] = useState<string>('');
  const [worseningDuration, setWorseningDuration] = useState<string>('');
  const [triggers, setTriggers] = useState<string[]>([]);
  const [headacheFreeDays, setHeadacheFreeDays] = useState<string>('');
  const [headacheDaysPerMonth, setHeadacheDaysPerMonth] = useState<string>('');
  const [comorbidities, setComorbidities] = useState<string[]>([]);
  const [radiologyExam, setRadiologyExam] = useState<string>('');
  const [radiologyDate, setRadiologyDate] = useState<string>('');
  const [radiologyLocation, setRadiologyLocation] = useState<string>('');
  const [radiologyResult, setRadiologyResult] = useState<string>('');
  const [additionalRadiologyInfo, setAdditionalRadiologyInfo] =
    useState<string>('');
  const [initialFrequency, setInitialFrequency] = useState<string>('');

  const [otherConditionDetails, setOtherConditionDetails] =
    useState<string>('');
  const [heredityMigraine, setHeredityMigraine] = useState<string[]>([]);
  const [neurologicalFamilyDiseases, setNeurologicalFamilyDiseases] =
    useState<string>('');
  const [neurologicalFamilyDiagnosis, setNeurologicalFamilyDiagnosis] =
    useState<string>('');
  const [neurologicalFamilyRelation, setNeurologicalFamilyRelation] =
    useState<string>('');
  const [otherConditions, setOtherConditions] = useState<string>('');
  const [hypertension, setHypertension] = useState<string>('');
  const [bloodPressureLastYear, setBloodPressureLastYear] =
    useState<string>('');
  const [weight, setWeight] = useState<string>('');
  const [height, setHeight] = useState<string>('');
  const [biologicalSex, setBiologicalSex] = useState<string>('');
  const [pregnant, setPregnant] = useState<string>('');
  const [pregnancyWeek, setPregnancyWeek] = useState<string>('');
  const [pregnancyPlan, setPregnancyPlan] = useState<string>('');
  const [contraceptiveUse, setContraceptiveUse] = useState<string>('');
  const [contraceptiveType, setContraceptiveType] = useState<string>('');
  const [breastfeeding, setBreastfeeding] = useState<string>('');
  const [additionalInfo, setAdditionalInfo] = useState<string>('');
  const [additionalInfoText, setAdditionalInfoText] = useState<string>('');

  useFormVersionTag('newVisit', NEW_VISIT_FORM_VERSION);

  const [preventiveMedicationHeadic, setPreventiveMedicationHeadic] =
    useState<string>('');

  const [
    preventiveMedicationSideEffectsHeadic,
    setPreventiveMedicationSideEffectsHeadic,
  ] = useState<string>('');

  const [
    preventiveMedicationSideEffectDetailsHeadic,
    setPreventiveMedicationSideEffectDetailsHeadic,
  ] = useState<string>('');

  const [
    hasAdditionalPreventiveMedicationHeadic,
    setHasAdditionalPreventiveMedicationHeadache,
  ] = useState<string>('');
  const [hasPreviousPreventiveMedication, setHasPreviousPreventiveMedication] =
    useState<string>('');

  const [otherPreventiveMedication, setOtherPreventiveMedication] =
    useState<string>('');
  const [otherPreventiveMedicationName, setOtherPreventiveMedicationName] =
    useState<string>('');
  const [otherPreventiveMedicationOther, setOtherPreventiveMedicationOther] =
    useState<string>('');
  const [
    otherPreventiveMedicationOtherNo,
    setOtherPreventiveMedicationOtherNo,
  ] = useState<string>('');
  const [
    otherPreventiveMedicationOtherNoWhatReaktion,
    setOtherPreventiveMedicationOtherNoWhatReaktion,
  ] = useState<string>('');
  const [describeAnomaly, setDescribeAnomaly] = useState<string>('');
  const [controllMrt, setControllMrt] = useState('');
  const [mrtDate, setMrtDate] = useState('');
  const [mrtLocation, setMrtLocation] = useState('');
  const [mrtResult, setMrtResult] = useState('');
  const [
    otherPreventiveMedicationOtherNoWitch,
    setOtherPreventiveMedicationOtherNoWitch,
  ] = useState('');
  const [otherAlergy, setOtherAlergy] = useState('');

  const [userAnswers, setUserAnswers] = useState<UserAnswers | null>(null);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const errorRef = useRef<HTMLParagraphElement>(null);

  const [additionalPreventiveMedications, setAdditionalPreventiveMedications] =
    useState<AdditionalPreventiveMedication[]>([]);

  const [previousPreventiveMedications, setPreviousPreventiveMedications] =
    useState<PreviousPreventiveMedication[]>([]);

  const [otherMedications, setOtherMedications] = useState<OtherMedication[]>(
    []
  );

  const [allergies, setAllergies] = useState<Allergy[]>([]);

  const [additionalAttackMedications, setAdditionalAttackMedications] =
    useState<Medication[]>([]);

  const [additionalPreviousMedications, setAdditionalPreviousMedications] =
    useState<PreviousMedication[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [diagnosedConditionsDateMigran, setDiagnosedConditionsDateMigran] =
    useState<string>('');
  const [doseTwo, setDoseTwo] = useState<string>('');
  const [
    additionalPainMedicationFrequencyTwo,
    setAdditionalPainMedicationFrequencyTwo,
  ] = useState<string>('');
  const [previousMedicationStopYearTwo, setPreviousMedicationStopYearTwo] =
    useState<string>('');
  const [
    previousMedicationSideEffectBiverkan,
    setPreviousMedicationSideEffectBiverkan,
  ] = useState<string>('');
  const [
    previousMedicationSideEffectSkal,
    setPreviousMedicationSideEffectSkal,
  ] = useState<string>('');
  const [
    previousMedicationSideEffectBiverkanSecond,
    setPreviousMedicationSideEffectBiverkanSecond,
  ] = useState<string>('');
  const [
    previousMedicationSideEffectSkalSecond,
    setPreviousMedicationSideEffectSkalSecond,
  ] = useState<string>('');
  const [
    previousMedicationStopYearSecond,
    setPreviousMedicationStopYearSecond,
  ] = useState<string>('');
  const [
    hasOtherPreviousPainMedicationStopMedication,
    setHasOtherPreviousPainMedicationStopMedication,
  ] = useState<string>('');
  const [
    otherPreventiveMedicationOtherOne,
    setOtherPreventiveMedicationOtherOne,
  ] = useState<string>('');
  const [
    otherPreventiveMedicationOtherTwo,
    setOtherPreventiveMedicationOtherTwo,
  ] = useState<string>('');
  const [
    otherPreventiveMedicationOtherThree,
    setOtherPreventiveMedicationOtherThree,
  ] = useState<string>('');
  const [
    otherPreventiveMedicationNameOne,
    setOtherPreventiveMedicationNameOne,
  ] = useState<string>('');
  const [
    otherPreventiveMedicationNameTwo,
    setOtherPreventiveMedicationNameTwo,
  ] = useState<string>('');
  const [
    otherPreventiveMedicationNameThree,
    setOtherPreventiveMedicationNameThree,
  ] = useState<string>('');

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [additionalAttackMedicine] = useState<AdditionalAttackMedication[]>([]);

  const [additionalHeadaches, setAdditionalHeadaches] = useState<
    AdditionalHeadache[]
  >([]);

  /* eslint-disable @typescript-eslint/no-shadow -- the hydrate effects destructure saved answers under the same names as the state they fill */
  useEffect(() => {
    const additionalHeadaches =
      userAnswers?.data?.formQuestionnaire?.data?.additionalHeadaches;

    if (additionalHeadaches) {
      setAdditionalHeadaches(additionalHeadaches);
    }
  }, [userAnswers]);

  useEffect(() => {
    const additionalAttackMedications =
      userAnswers?.data?.formQuestionnaire?.data?.additionalAttackMedications;

    if (additionalAttackMedications) {
      setAdditionalAttackMedications(additionalAttackMedications);
    }
  }, [userAnswers]);

  useEffect(() => {
    const additionalPreviousMedications =
      userAnswers?.data?.formQuestionnaire?.data?.additionalPreviousMedications;

    if (additionalPreviousMedications) {
      setAdditionalPreviousMedications(additionalPreviousMedications);
    }
  }, [userAnswers]);

  useEffect(() => {
    const additionalPreventiveMedications =
      userAnswers?.data?.formQuestionnaire?.data
        ?.additionalPreventiveMedications;

    if (additionalPreventiveMedications) {
      setAdditionalPreventiveMedications(additionalPreventiveMedications);
    }
  }, [userAnswers]);

  useEffect(() => {
    const previousPreventiveMedications =
      userAnswers?.data?.formQuestionnaire?.data?.previousPreventiveMedications;

    if (previousPreventiveMedications) {
      setPreviousPreventiveMedications(previousPreventiveMedications);
    }
  }, [userAnswers]);

  useEffect(() => {
    const otherMedications =
      userAnswers?.data?.formQuestionnaire?.data?.otherMedications;

    if (otherMedications) {
      setOtherMedications(otherMedications);
    }
  }, [userAnswers]);

  useEffect(() => {
    const saved = userAnswers?.data?.formQuestionnaire?.data;

    if (typeof saved?.pregnancyWeek === 'number') {
      setPregnancyWeek(String(saved.pregnancyWeek));
    }
    if (saved?.breastfeeding) {
      setBreastfeeding(saved.breastfeeding);
    }
  }, [userAnswers]);

  useEffect(() => {
    const allergies = userAnswers?.data?.formQuestionnaire?.data?.allergies;

    if (allergies) {
      setAllergies(allergies);
    }
    /* eslint-enable @typescript-eslint/no-shadow */
  }, [userAnswers]);

  const fetchUserAnswers = async () => {
    try {
      const result = await getMyNewVisit();
      setUserAnswers(result?.form ? { data: result.form } : null);
      setLoading(false);
    } catch (err) {
      reportError(err);
      setLoadError(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserAnswers();
  }, []);

  useEffect(() => {
    /* eslint-disable @typescript-eslint/no-shadow -- the hydrate effects destructure saved answers under the same names as the state they fill */
    const {
      hasMultipleHeadaches,
      ageFirstAttack,
      headacheDuration,
      painLocation,
      attackPattern,
      painCharacter,
      painIntensity,
      physicalActivityEffect,
      dizzinessType,
      preferredActionDuringAttack,
      dizzinessDuration,
      headacheFrequency,
      frequencyDuration,
      initialFrequency,
      worseningOverTime,
      headacheFreeDays,
      headacheDaysPerMonth,
      worseningDuration,
      medicationOveruse,
      hasWithdrawal,
      radiologyExam,
      controllMrt,
      takesPainMedication,
      neurologicalFamilyDiseases,
      otherConditions,
      hypertension,
      bloodPressureLastYear,
      biologicalSex,
      additionalInfo,
      additionalInfoText,
      hasPreviousPainMedication,
      diagnosedConditionsDateMigran,
      diagnosisConditionsData,
      diagnosedConditionsDateSpanningshuvudvark,
      diagnosisConditionsDataSpanningshuvudvark,
      diagnosedConditionsDateHorton,
      diagnosisConditionsDataHorton,
      attackPatternDuring,
      comorbidities,
      nonMedicationTreatments,
      bettskenEffect,
      massageEffect,
      acupunctureEffect,
      tensEffect,
      yogaEffect,
      naprapatEffect,
      hasPreviousPreventiveMedication,
      otherPreventiveMedication,
      otherPreventiveMedicationOtherNo,
      heredityMigraine,
      weight,
      height,
      mrtDate,
      mrtLocation,
      mrtResult,
      pregnant,
      pregnancyPlan,
      contraceptiveUse,
      contraceptiveType,
      painMedicationName,
      painMedicationDose,
      painMedicationEffect,
      painMedicationEffectiveness,
      painMedicationFrequency,
      hasAdditionalAttackMedication,
      neurologicalFamilyDiagnosis,
      neurologicalFamilyRelation,
      overusedMedication,
      overuseDuration,
      hasOtherPreviousPainMedicationStopSecond,
      hasOtherPreviousPainMedicationStop,
      previousMedicationName,
      previousMedicationNameSecond,
      previousMedicationDose,
      previousMedicationDoseSecond,
      previousMedicationFrequency,
      previousMedicationFrequencySecond,
      previousMedicationStartYear,
      previousMedicationStartYearSecond,
      previousMedicationStopReason,
      previousMedicationStopReasonSecond,
      previousMedicationStopYear,
      radiologyDate,
      radiologyLocation,
      radiologyResult,
      describeAnomaly,
      withdrawalDate,
      withdrawalEffect,
      otherPreventiveMedicationOtherNoWitch,
      otherPreventiveMedicationOtherNoWhatReaktion,
      otherAlergy,
      otherHeadacheType,
      diagnosedConditionsDateOther,
      previousMedicationOtherReason,
      previousMedicationOtherReasonTwo,
      preventiveMedicationHeadic,
      preventiveMedicationNameHeadic,
      preventiveMedicationDoseHeadic,
      preventiveMedicationEffectivenessHeadic,
      preventiveMedicationStartYearHeadic,
      preventiveMedicationSideEffectsHeadic,
      preventiveMedicationSideEffectDetailsHeadic,
      otherPreventiveMedicationName,
      hasAdditionalPreventiveMedicationHeadic,
      updateMedicationTwo,
      doseTwo,
      additionalPainMedicationFrequencyTwo,
      previousMedicationStopYearTwo,
      previousMedicationSideEffectSkal,
      previousMedicationSideEffectBiverkan,
      previousMedicationSideEffectBiverkanSecond,
      previousMedicationSideEffectSkalSecond,
      previousMedicationStopYearSecond,
      hasOtherPreviousPainMedicationStopMedication,
      associatedSymptoms,
      additionalRadiologyInfo,
      bloodPressureValue,
      otherConditionDetails,
      otherPreventiveMedicationExept,
      otherPreventiveMedicationOther,
      otherPreventiveMedicationOtherOne,
      otherPreventiveMedicationOtherTwo,
      otherPreventiveMedicationOtherThree,
      otherPreventiveMedicationNameOne,
      otherPreventiveMedicationNameTwo,
      otherPreventiveMedicationNameThree,
    } = setPatientFormValue(
      userAnswers,
      setDiagnosedConditions,
      setAssociatedSymptoms,
      setAuraSymptoms,
      setTriggers
    );

    setHasMultipleHeadaches(hasMultipleHeadaches);
    setAgeFirstAttack(ageFirstAttack);
    setHeadacheDuration(headacheDuration);
    setPainLocation(painLocation);
    setAttackPattern(attackPattern);
    setPainCharacter(painCharacter);
    setPainIntensity(painIntensity);
    setPhysicalActivityEffect(physicalActivityEffect);
    setPreferredActionDuringAttack(preferredActionDuringAttack);
    setDizzinessDuration(dizzinessDuration);
    setHeadacheFrequency(headacheFrequency);
    setFrequencyDuration(frequencyDuration);
    setInitialFrequency(initialFrequency);
    setWorseningOverTime(worseningOverTime);
    setHeadacheFreeDays(headacheFreeDays);
    setHeadacheDaysPerMonth(headacheDaysPerMonth);
    setWorseningDuration(worseningDuration);
    setMedicationOveruse(medicationOveruse);
    setHasWithdrawal(hasWithdrawal);
    setRadiologyExam(radiologyExam);
    setControllMrt(controllMrt);
    setTakesPainMedication(takesPainMedication);
    setNeurologicalFamilyDiseases(neurologicalFamilyDiseases);
    setOtherConditions(otherConditions);
    setHypertension(hypertension);
    setBloodPressureLastYear(bloodPressureLastYear);
    setBiologicalSex(biologicalSex);
    setAdditionalInfo(additionalInfo);
    setAdditionalInfoText(additionalInfoText);
    setHasPreviousPainMedication(hasPreviousPainMedication);
    setDiagnosedConditionsDateMigran(diagnosedConditionsDateMigran);
    setDiagnosedConditionsDateSpanningshuvudvark(
      diagnosedConditionsDateSpanningshuvudvark
    );
    setDiagnosisConditionsData(diagnosisConditionsData);
    setDiagnosisConditionsDataSpanningshuvudvark(
      diagnosisConditionsDataSpanningshuvudvark
    );
    setDiagnosisConditionsDataHorton(diagnosisConditionsDataHorton);
    setDiagnosedConditionsDateHorton(diagnosedConditionsDateHorton);
    setAttackPatternDuring(attackPatternDuring);
    setComorbidities(comorbidities);
    setNonMedicationTreatments(nonMedicationTreatments);
    setBettskenEffect(bettskenEffect);
    setMassageEffect(massageEffect);
    setAcupunctureEffect(acupunctureEffect);
    setTensEffect(tensEffect);
    setYogaEffect(yogaEffect);
    setNaprapatEffect(naprapatEffect);

    setHasPreviousPreventiveMedication(hasPreviousPreventiveMedication);
    setOtherPreventiveMedication(otherPreventiveMedication);
    setOtherPreventiveMedicationOtherNo(otherPreventiveMedicationOtherNo);
    setHeredityMigraine(heredityMigraine);
    setWeight(weight);
    setHeight(height);
    setMrtDate(mrtDate);
    setMrtLocation(mrtLocation);
    setMrtResult(mrtResult);
    setPregnant(pregnant);
    setPregnancyPlan(pregnancyPlan);
    setContraceptiveUse(contraceptiveUse);
    setContraceptiveType(contraceptiveType);
    setPainMedicationName(painMedicationName);
    setPainMedicationDose(painMedicationDose);
    setPainMedicationEffect(painMedicationEffect);
    setPainMedicationEffectiveness(painMedicationEffectiveness);
    setPainMedicationFrequency(painMedicationFrequency);
    setHasAdditionalAttackMedication(hasAdditionalAttackMedication);
    setNeurologicalFamilyDiagnosis(neurologicalFamilyDiagnosis);
    setNeurologicalFamilyRelation(neurologicalFamilyRelation);
    setOverusedMedication(overusedMedication);
    setOveruseDuration(overuseDuration);
    setHasOtherPreviousPainMedicationStopSecond(
      hasOtherPreviousPainMedicationStopSecond
    );
    setHasOtherPreviousPainMedicationStop(hasOtherPreviousPainMedicationStop);
    setPreviousMedicationName(previousMedicationName);
    setPreviousMedicationNameSecond(previousMedicationNameSecond);
    setPreviousMedicationDose(previousMedicationDose);
    setPreviousMedicationDoseSecond(previousMedicationDoseSecond);
    setPreviousMedicationFrequency(previousMedicationFrequency);
    setPreviousMedicationFrequencySecond(previousMedicationFrequencySecond);
    setPreviousMedicationStartYear(previousMedicationStartYear);
    setPreviousMedicationStartYearSecond(previousMedicationStartYearSecond);
    setPreviousMedicationStopReason(previousMedicationStopReason);
    setPreviousMedicationStopReasonSecond(previousMedicationStopReasonSecond);
    setPreviousMedicationStopYear(previousMedicationStopYear);
    setRadiologyDate(radiologyDate);
    setRadiologyLocation(radiologyLocation);
    setRadiologyResult(radiologyResult);
    setDescribeAnomaly(describeAnomaly);
    setWithdrawalDate(withdrawalDate);
    setWithdrawalEffect(withdrawalEffect);
    setOtherPreventiveMedicationOtherNoWitch(
      otherPreventiveMedicationOtherNoWitch
    );
    setOtherPreventiveMedicationOtherNoWhatReaktion(
      otherPreventiveMedicationOtherNoWhatReaktion
    );
    setOtherAlergy(otherAlergy);
    setOtherHeadacheType(otherHeadacheType);
    setDiagnosedConditionsDateOther(diagnosedConditionsDateOther);
    setPreviousMedicationOtherReason(previousMedicationOtherReason);
    setPreviousMedicationOtherReasonTwo(previousMedicationOtherReasonTwo);
    setPreventiveMedicationHeadic(preventiveMedicationHeadic);
    setPreventiveMedicationNameHeadic(preventiveMedicationNameHeadic);
    setPreventiveMedicationDoseHeadic(preventiveMedicationDoseHeadic);
    setPreventiveMedicationEffectivenessHeadic(
      preventiveMedicationEffectivenessHeadic
    );
    setPreventiveMedicationStartYearHeadic(preventiveMedicationStartYearHeadic);
    setPreventiveMedicationSideEffectsHeadic(
      preventiveMedicationSideEffectsHeadic
    );
    setPreventiveMedicationSideEffectDetailsHeadic(
      preventiveMedicationSideEffectDetailsHeadic
    );
    setHasAdditionalPreventiveMedicationHeadache(
      hasAdditionalPreventiveMedicationHeadic
    );
    setUpdateMedicationTwo(updateMedicationTwo);
    setDoseTwo(doseTwo);
    setAdditionalPainMedicationFrequencyTwo(
      additionalPainMedicationFrequencyTwo
    );
    setPreviousMedicationStopYearTwo(previousMedicationStopYearTwo);
    setPreviousMedicationSideEffectSkal(previousMedicationSideEffectSkal);
    setPreviousMedicationSideEffectBiverkan(
      previousMedicationSideEffectBiverkan
    );
    setPreviousMedicationSideEffectBiverkanSecond(
      previousMedicationSideEffectBiverkanSecond
    );
    setPreviousMedicationSideEffectSkalSecond(
      previousMedicationSideEffectSkalSecond
    );
    setPreviousMedicationStopYearSecond(previousMedicationStopYearSecond);
    setHasOtherPreviousPainMedicationStopMedication(
      hasOtherPreviousPainMedicationStopMedication
    );
    setAssociatedSymptoms(associatedSymptoms);
    setAdditionalRadiologyInfo(additionalRadiologyInfo);
    setBloodPressureValue(bloodPressureValue);
    setOtherConditionDetails(otherConditionDetails);
    setOtherPreventiveMedicationExept(otherPreventiveMedicationExept);
    setOtherPreventiveMedicationOther(otherPreventiveMedicationOther);
    setOtherPreventiveMedicationOtherOne(otherPreventiveMedicationOtherOne);
    setOtherPreventiveMedicationOtherTwo(otherPreventiveMedicationOtherTwo);
    setOtherPreventiveMedicationOtherThree(otherPreventiveMedicationOtherThree);
    setOtherPreventiveMedicationNameOne(otherPreventiveMedicationNameOne);
    setOtherPreventiveMedicationNameTwo(otherPreventiveMedicationNameTwo);
    setOtherPreventiveMedicationNameThree(otherPreventiveMedicationNameThree);
    setOtherPreventiveMedicationName(otherPreventiveMedicationName);
    setDizzinessType(dizzinessType);

    setFormSubmitted(userAnswers?.data?.status === 'submitted');
  }, [userAnswers]);
  /* eslint-enable @typescript-eslint/no-shadow */

  const addNewPreventiveMedication = () => {
    setAdditionalPreventiveMedications([
      ...additionalPreventiveMedications,
      {
        name: '',
        dose: '',
        effect: '',
        frequency: '',
        startYear: '',
        sideEffects: '',
        sideEffectDetails: '',
      },
    ]);
  };

  const addNewPreviousPreventiveMedication = () => {
    setPreviousPreventiveMedications([
      ...previousPreventiveMedications,
      {
        name: '',
        dose: '',
        startYear: '',
        stopReason: '',
        sideEffects: '',
        stopReasonDetails: '',
        stopYear: '',
      },
    ]);
  };

  const addNewOtherMedication = () => {
    setOtherMedications([
      ...otherMedications,
      {
        name: '',
      },
    ]);
  };

  const data = transformFormData(
    diagnosedConditions,
    otherHeadacheType,
    diagnosedConditionsDateOther,
    hasMultipleHeadaches,
    ageFirstAttack,
    headacheDuration,
    painLocation,
    attackPattern,
    painCharacter,
    painIntensity,
    physicalActivityEffect,
    preferredActionDuringAttack,
    associatedSymptoms,
    auraSymptoms,
    dizzinessType,
    dizzinessDuration,
    headacheFrequency,
    frequencyDuration,
    initialFrequency,
    worseningOverTime,
    worseningDuration,
    triggers,
    comorbidities,
    headacheFreeDays,
    nonMedicationTreatments,
    medicationOveruse,
    hasWithdrawal,
    radiologyExam,
    controllMrt,
    takesPainMedication,
    heredityMigraine,
    neurologicalFamilyDiseases,
    otherConditions,
    hypertension,
    weight,
    height,
    biologicalSex,
    pregnant,
    additionalInfo,
    additionalInfoText,
    massageEffect,
    naprapatEffect,
    acupunctureEffect,
    tensEffect,
    yogaEffect,
    bettskenEffect,
    overuseDuration,
    withdrawalDate,
    withdrawalEffect,
    radiologyDate,
    radiologyLocation,
    radiologyResult,
    describeAnomaly,
    painMedicationName,
    painMedicationDose,
    painMedicationEffect,
    painMedicationEffectiveness,
    painMedicationFrequency,
    pregnancyPlan,
    contraceptiveUse,
    contraceptiveType,
    hasPreviousPainMedication,
    diagnosedConditionsDateMigran,
    diagnosisConditionsData,
    diagnosisConditionsDataSpanningshuvudvark,
    diagnosedConditionsDateSpanningshuvudvark,
    diagnosisConditionsDataHorton,
    diagnosedConditionsDateHorton,
    attackPatternDuring,
    mrtDate,
    mrtLocation,
    mrtResult,
    hasAdditionalAttackMedication,
    neurologicalFamilyDiagnosis,
    neurologicalFamilyRelation,
    overusedMedication,
    hasOtherPreviousPainMedicationStopSecond,
    hasOtherPreviousPainMedicationStop,
    previousMedicationName,
    previousMedicationNameSecond,
    previousMedicationDose,
    previousMedicationDoseSecond,
    previousMedicationFrequency,
    previousMedicationFrequencySecond,
    previousMedicationStartYear,
    previousMedicationStartYearSecond,
    previousMedicationStopReason,
    previousMedicationStopReasonSecond,
    previousMedicationStopYear,
    otherPreventiveMedicationOtherNo,
    otherPreventiveMedicationOtherNoWitch,
    otherPreventiveMedicationOtherNoWhatReaktion,
    otherAlergy,
    previousMedicationOtherReason,
    previousMedicationSideEffect,
    previousMedicationOtherReasonTwo,
    preventiveMedicationHeadic,
    preventiveMedicationNameHeadic,
    preventiveMedicationDoseHeadic,
    preventiveMedicationEffectivenessHeadic,
    preventiveMedicationStartYearHeadic,
    preventiveMedicationSideEffectsHeadic,
    preventiveMedicationSideEffectDetailsHeadic,
    hasAdditionalPreventiveMedicationHeadic,
    updateMedicationTwo,
    doseTwo,
    additionalPainMedicationFrequencyTwo,
    previousMedicationStopYearTwo,
    previousMedicationSideEffectSkal,
    previousMedicationSideEffectBiverkan,
    previousMedicationSideEffectBiverkanSecond,
    previousMedicationSideEffectSkalSecond,
    previousMedicationStopYearSecond,
    hasOtherPreviousPainMedicationStopMedication,
    additionalRadiologyInfo,
    additionalAttackMedicine,
    bloodPressureValue,
    bloodPressureLastYear,
    otherConditionDetails,
    hasPreviousPreventiveMedication,
    otherPreventiveMedicationExept,
    otherPreventiveMedicationOther,
    otherPreventiveMedicationOtherOne,
    otherPreventiveMedicationOtherTwo,
    otherPreventiveMedicationOtherThree,
    otherPreventiveMedicationNameOne,
    otherPreventiveMedicationNameTwo,
    otherPreventiveMedicationNameThree,
    otherPreventiveMedicationName,
    otherPreventiveMedication,
    headacheDaysPerMonth
  );

  const { unanswered, showUnanswered, incompleteRef, blockIfIncomplete } =
    useUnansweredGate({
      diagnosedConditions,
      hasMultipleHeadaches,
      ageFirstAttack,
      headacheDuration,
      painLocation,
      attackPattern,
      painCharacter,
      painIntensity,
      physicalActivityEffect,
      preferredActionDuringAttack,
      associatedSymptoms,
      auraSymptoms,
      headacheFrequency,
      frequencyDuration,
      initialFrequency,
      worseningOverTime,
      triggers,
      headacheDaysPerMonth,
      comorbidities,
      nonMedicationTreatments,
      medicationOveruse,
      hasWithdrawal,
      radiologyExam,
      controllMrt,
      takesPainMedication,
      heredityMigraine,
      neurologicalFamilyDiseases,
      otherConditions,
      hypertension,
      bloodPressureLastYear,
      weight,
      biologicalSex,
      additionalInfo,
    });
  const submitDisabled = isSubmitting || !confirmChecked;

  const pregnancyAnswers = useMemo(
    () => ({
      pregnancyWeek: pregnancyWeek === '' ? undefined : Number(pregnancyWeek),
      breastfeeding,
    }),
    [pregnancyWeek, breastfeeding]
  );

  const autoSaveFn = useCallback(async () => {
    const formData = {
      ...data,
      ...pregnancyAnswers,
      formVersion: NEW_VISIT_FORM_VERSION,
      additionalHeadaches,
      additionalAttackMedications,
      additionalPreviousMedications,
      additionalPreventiveMedications,
      previousPreventiveMedications,
      otherMedications,
      allergies,
    };
    await saveMyNewVisit(formData, 'inProgress');
  }, [
    data,
    pregnancyAnswers,
    additionalHeadaches,
    additionalAttackMedications,
    additionalPreviousMedications,
    additionalPreventiveMedications,
    previousPreventiveMedications,
    otherMedications,
    allergies,
  ]);

  const { trigger: handleAutoSave, cancel: cancelAutoSave } =
    useAutoSave(autoSaveFn);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submitter = (e.nativeEvent as SubmitEvent)
      ?.submitter as HTMLButtonElement | null;
    const isFinalSubmit = submitter?.name === 'SaveForm';

    if (blockIfIncomplete(isFinalSubmit)) return;

    cancelAutoSave();
    setIsSubmitting(true);
    setError(undefined);

    try {
      const formData = {
        ...data,
        ...pregnancyAnswers,
        formVersion: NEW_VISIT_FORM_VERSION,
        additionalHeadaches,
        additionalAttackMedications,
        additionalPreviousMedications,
        additionalPreventiveMedications,
        previousPreventiveMedications,
        otherMedications,
        allergies,
      };

      const status: 'inProgress' | 'submitted' = isFinalSubmit
        ? 'submitted'
        : 'inProgress';

      await saveMyNewVisit(formData, status);
      setFormSubmitted(isFinalSubmit);
    } catch (err) {
      reportError(err);
      setError('Det gick inte att skicka formuläret. Försök igen.');
      setTimeout(
        () =>
          errorRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          }),
        0
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (formSubmitted) {
    return (
      <FormSubmittedView
        title="Tack för dina svar!"
        message="Du kommer nu bli inbokad till en läkartid. De flesta patienter får en bokad tid direkt, men det kan ta upp till några dagar. När du blivit inbokad får du en bekräftelse via SMS och i appen. Du kan redan nu börja fylla i din dagbok"
      />
    );
  }

  if (loadError) {
    return (
      <FormLayout title="Nybesöksformulär" loading={false}>
        <Typography sx={{ color: DS.inkSecondary, textAlign: 'center', py: 4 }}>
          Formuläret kunde inte laddas. Försök igen senare.
        </Typography>
      </FormLayout>
    );
  }

  const addNewAttackMedication = () => {
    setAdditionalAttackMedications([
      ...additionalAttackMedications,
      {
        name: '',
        dose: '',
        effect: '',
        effectiveness: '',
        frequency: '',
      },
    ]);
  };

  const addNewPreviousMedication = () => {
    setAdditionalPreviousMedications([
      ...additionalPreviousMedications,
      {
        name: '',
        dose: '',
        frequency: '',
        startYear: '',
        stopReason: '',
        sideEffects: '',
        stopReasonDetails: '',
        stopYear: '',
      },
    ]);
  };

  return (
    <form noValidate onSubmit={handleFormSubmit}>
      <FormLayout
        title="Nybesök Läkare"
        description="Det här är ett omfattande formulär med syftet att ge läkaren underlag för att kunna hjälpa dig på bästa sätt med din huvudvärksproblematik. Du får svara på många frågor, dina svar sparas automatiskt och du kan avbryta och fortsätta senare om du vill ta en paus. Längst ner på sidan finns en Skicka-knapp som du klickar på när du har svarat på alla frågor."
        loading={loading}
      >
        <>
          <Box id="q-2" sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Har du en diagnostiserad huvudvärksjukdom sedan tidigare?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                'Migrän',
                'Spänningshuvudvärk',
                'Horton',
                'Annan huvudvärk',
                'Nej',
              ].map((option) => (
                <Box
                  component="label"
                  key={option}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    onBlur={handleAutoSave}
                    value={option}
                    checked={diagnosedConditions.includes(option)}
                    onChange={(e) => {
                      const { value } = e.target;

                      setDiagnosedConditions((prev) => {
                        let updated: string[];

                        if (prev.includes(value)) {
                          updated = prev.filter((item) => item !== value);

                          switch (value) {
                            case 'Migrän':
                              setDiagnosedConditionsDateMigran('');
                              setDiagnosisConditionsData('');
                              break;
                            case 'Spänningshuvudvärk':
                              setDiagnosedConditionsDateSpanningshuvudvark('');
                              setDiagnosisConditionsDataSpanningshuvudvark('');
                              break;
                            case 'Horton':
                              setDiagnosedConditionsDateHorton('');
                              setDiagnosisConditionsDataHorton('');
                              break;
                            case 'Annan huvudvärk':
                              setOtherHeadacheType('');
                              setDiagnosedConditionsDateOther('');
                              break;
                            default:
                              break;
                          }
                        } else {
                          updated =
                            value === 'Nej'
                              ? ['Nej']
                              : prev.filter((v) => v !== 'Nej').concat(value);

                          if (value === 'Nej') {
                            setDiagnosedConditionsDateMigran('');
                            setDiagnosisConditionsData('');
                            setDiagnosedConditionsDateSpanningshuvudvark('');
                            setDiagnosisConditionsDataSpanningshuvudvark('');
                            setDiagnosedConditionsDateHorton('');
                            setDiagnosisConditionsDataHorton('');
                            setOtherHeadacheType('');
                            setDiagnosedConditionsDateOther('');
                          }
                        }

                        return updated;
                      });
                    }}
                    style={{ marginRight: '8px' }}
                  />
                  <Typography
                    sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                  >
                    {option === 'Horton' ? 'Klusterhuvudvärk' : option}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
          {diagnosedConditions.includes('Migrän') && (
            <Box
              sx={{
                mt: 3,
                mb: 3,
                p: 2,
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                backgroundColor: '#F0EDE6',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '18px',
                  mb: 2,
                  fontFamily: DS.fontBody,
                }}
              >
                Migrän
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: '#374151',
                    mb: 1,
                    fontFamily: DS.fontBody,
                    fontSize: '16px',
                  }}
                >
                  Vilket år ställdes diagnosen migrän? Vid osäkerhet välj
                  intervall.
                </Typography>
                <input
                  type="text"
                  onBlur={handleAutoSave}
                  placeholder="Ex: 2005-2010"
                  value={diagnosedConditionsDateMigran || ''}
                  onChange={(e) =>
                    setDiagnosedConditionsDateMigran(e.target.value)
                  }
                  style={inputStyle}
                />
              </Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Vilken typ av läkare ställde diagnosen migrän?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {[
                  'Allmänläkare',
                  'Neurolog',
                  'Smärtläkare',
                  'Akutläkare',
                  'Annan specialistläkare',
                  'Vet ej',
                ].map((doctorTypes) => (
                  <Box
                    component="label"
                    key={doctorTypes}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      onBlur={handleAutoSave}
                      name="diagnosisConditionsData"
                      value={doctorTypes}
                      checked={diagnosisConditionsData === doctorTypes}
                      onChange={() => setDiagnosisConditionsData(doctorTypes)}
                      style={{ marginRight: '8px' }}
                    />
                    <Typography
                      sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                    >
                      {doctorTypes}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {diagnosedConditions.includes('Spänningshuvudvärk') && (
            <Box
              sx={{
                mt: 3,
                mb: 3,
                p: 2,
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                backgroundColor: '#F0EDE6',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '18px',
                  mb: 2,
                  fontFamily: DS.fontBody,
                }}
              >
                Spänningshuvudvärk
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: '#374151',
                    mb: 1,
                    fontFamily: DS.fontBody,
                    fontSize: '16px',
                  }}
                >
                  Vilket år ställdes diagnosen spänningshuvudvärk? Vid osäkerhet
                  välj intervall.
                </Typography>
                <input
                  type="text"
                  onBlur={handleAutoSave}
                  placeholder="Ex: 2005-2010"
                  value={diagnosedConditionsDateSpanningshuvudvark || ''}
                  onChange={(e) =>
                    setDiagnosedConditionsDateSpanningshuvudvark(e.target.value)
                  }
                  style={inputStyle}
                />
              </Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Vilken typ av läkare ställde diagnosen migrän?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {[
                  'Allmänläkare',
                  'Neurolog',
                  'Smärtläkare',
                  'Akutläkare',
                  'Annan specialistläkare',
                  'Vet ej',
                ].map((doctorTypesSpanningshuvudvark) => (
                  <Box
                    component="label"
                    key={doctorTypesSpanningshuvudvark}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      onBlur={handleAutoSave}
                      name="diagnosisConditionsDataSpanningshuvudvark"
                      value={doctorTypesSpanningshuvudvark}
                      checked={
                        diagnosisConditionsDataSpanningshuvudvark ===
                        doctorTypesSpanningshuvudvark
                      }
                      onChange={() =>
                        setDiagnosisConditionsDataSpanningshuvudvark(
                          doctorTypesSpanningshuvudvark
                        )
                      }
                      style={{ marginRight: '8px' }}
                    />
                    <Typography
                      sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                    >
                      {doctorTypesSpanningshuvudvark}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {diagnosedConditions.includes('Horton') && (
            <Box
              sx={{
                mt: 3,
                mb: 3,
                p: 2,
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                backgroundColor: '#F0EDE6',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '18px',
                  mb: 2,
                  fontFamily: DS.fontBody,
                }}
              >
                Klusterhuvudvärk
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: '#374151',
                    mb: 1,
                    fontFamily: DS.fontBody,
                    fontSize: '16px',
                  }}
                >
                  Vilket år ställdes diagnosen klusterhuvudvärk? Vid osäkerhet
                  välj intervall.
                </Typography>
                <input
                  type="text"
                  onBlur={handleAutoSave}
                  placeholder="Ex: 2005-2010"
                  value={diagnosedConditionsDateHorton || ''}
                  onChange={(e) =>
                    setDiagnosedConditionsDateHorton(e.target.value)
                  }
                  style={inputStyle}
                />
              </Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Vilken typ av läkare ställde diagnosen klusterhuvudvärk?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {[
                  'Allmänläkare',
                  'Neurolog',
                  'Smärtläkare',
                  'Akutläkare',
                  'Annan specialistläkare',
                  'Vet ej',
                ].map((doctorTypesHorton) => (
                  <Box
                    component="label"
                    key={doctorTypesHorton}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      onBlur={handleAutoSave}
                      name="diagnosisConditionsDataHorton"
                      value={doctorTypesHorton}
                      checked={
                        diagnosisConditionsDataHorton === doctorTypesHorton
                      }
                      onChange={() =>
                        setDiagnosisConditionsDataHorton(doctorTypesHorton)
                      }
                      style={{ marginRight: '8px' }}
                    />
                    <Typography
                      sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                    >
                      {doctorTypesHorton}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {diagnosedConditions.includes('Annan huvudvärk') && (
            <Box
              sx={{
                mt: 3,
                mb: 3,
                p: 2,
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                backgroundColor: '#F0EDE6',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '18px',
                  mb: 2,
                  fontFamily: DS.fontBody,
                }}
              >
                Annan huvudvärk
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Ange vilken annan huvudvärk
              </Typography>
              <input
                type="text"
                onBlur={handleAutoSave}
                placeholder=" "
                value={otherHeadacheType || ''}
                onChange={(e) => setOtherHeadacheType(e.target.value)}
                style={{ ...inputStyle, marginTop: '8px' }}
              />
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                  mt: 2,
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Vilket år ställdes diagnosen? Vid osäkerhet välj intervall.
              </Typography>
              <input
                type="text"
                onBlur={handleAutoSave}
                placeholder="Ex: 2005-2010"
                value={diagnosedConditionsDateOther || ''}
                onChange={(e) =>
                  setDiagnosedConditionsDateOther(e.target.value)
                }
                style={inputStyle}
              />
            </Box>
          )}
        </>

        <Box>
          <Typography sx={{ my: 2, fontFamily: DS.fontBody, fontSize: '16px' }}>
            Följande 19 frågor handlar om din huvudvärk.
          </Typography>
          <Typography sx={{ my: 2, fontFamily: DS.fontBody, fontSize: '16px' }}>
            Har du uppgett att du lider av flera olika typer av huvudvärk kommer
            du få svara på samma 19 frågor för varje typ du har uppgett.
          </Typography>
        </Box>
        <Box id="q-3" sx={{ mb: 3 }}>
          <FormRadioGroup
            label="Har du flera olika typer av huvudvärk? (Samma huvudvärkskaraktär men med olika smärtintensitet räknas som en huvudvärk)."
            name="hasMultipleHeadaches"
            value={hasMultipleHeadaches}
            options={[
              { label: 'Ja', value: 'yes' },
              { label: 'Nej', value: 'no' },
            ]}
            onChange={(val) => setHasMultipleHeadaches(val)}
            onBlur={handleAutoSave}
          />
        </Box>
        <Box id="q-4" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: 600,
              color: DS.deepTeal,
              mb: 3,
              fontFamily: DS.fontDisplay,
            }}
          >
            Huvudvärk 1
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Vid vilken ålder hade du ditt första anfall?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {[
              '0-10 år',
              '11-20 år',
              '21-30 år',
              '31-40 år',
              '41-50 år',
              'mer än 50 år',
            ].map((ageRange) => (
              <Box
                component="label"
                key={ageRange}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  onBlur={handleAutoSave}
                  name="ageFirstAttack"
                  value={ageRange}
                  checked={ageFirstAttack === ageRange}
                  onChange={() => setAgeFirstAttack(ageRange)}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  {ageRange}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box id="q-5" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Hur länge varar ett obehandlat anfall?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {[
              'mindre än 1 timme',
              '1-3 timmar',
              '4-72 timmar',
              'mer än 72 timmar',
            ].map((duration) => (
              <Box
                component="label"
                key={duration}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  onBlur={handleAutoSave}
                  name="headacheDuration"
                  value={duration}
                  checked={headacheDuration === duration}
                  onChange={() => setHeadacheDuration(duration)}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  {duration}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box id="q-6" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Var sitter vanligtvis smärtan?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {[
              'i hela huvudet',
              'i halva huvudet',
              'som ett band runt huvudet',
              'i nacken/bakhuvudet',
              'i en specifik punkt eller litet område av huvudet',
              'i eller runt ena ögat eller i tinningen nära ena ögat',
            ].map((location) => (
              <Box
                component="label"
                key={location}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  onBlur={handleAutoSave}
                  name="painLocation"
                  value={location}
                  checked={painLocation === location}
                  onChange={() => setPainLocation(location)}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  {location}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box id="q-7" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Har du denna huvudvärk från minst en attack varannan dag upp till 8
            ggr dagligen?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="attackPattern"
                value="yes"
                checked={attackPattern === 'yes'}
                onChange={() => setAttackPattern('yes')}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Ja
              </Typography>
            </Box>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="attackPattern"
                value="no"
                checked={attackPattern === 'no'}
                onChange={() => {
                  setAttackPattern('no');
                  setAttackPatternDuring('');
                }}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Nej
              </Typography>
            </Box>
          </Box>
          {attackPattern === 'yes' && (
            <Box>
              <br />
              <Typography
                sx={{
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Har du dessa attacker under:
              </Typography>
              <Box
                component="label"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  onBlur={handleAutoSave}
                  name="attackPatternDuring"
                  value="yes"
                  checked={attackPatternDuring === 'yes'}
                  onChange={() => setAttackPatternDuring('yes')}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  Perioder av veckor-månader, sedan besvärsfrihet under
                  månader-år innan ny period börjar.
                </Typography>
              </Box>
              <br />
              <Box
                component="label"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  onBlur={handleAutoSave}
                  name="attackPatternDuring"
                  value="no"
                  checked={attackPatternDuring === 'no'}
                  onChange={() => setAttackPatternDuring('no')}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  Kontinuerligt sedan minst ett år tillbaka, möjligen med
                  uppehåll under mindre än 3 månader
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <Box id="q-8" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Vilken smärtkaraktär stämmer bäst med din huvudvärk?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {[
              'pulserande/dunkande',
              'molande',
              'skärande',
              'brännande/svidande',
              'huggande',
              'tryckande',
            ].map((character) => (
              <Box
                component="label"
                key={character}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  onBlur={handleAutoSave}
                  name="painCharacter"
                  value={character}
                  checked={painCharacter === character}
                  onChange={() => setPainCharacter(character)}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  {character}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box id="q-9" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Hur uttalad är smärtan när den är som värst?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {['lätt', 'måttlig', 'intensiv'].map((intensity) => (
              <Box
                component="label"
                key={intensity}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  onBlur={handleAutoSave}
                  name="painIntensity"
                  value={intensity}
                  checked={painIntensity === intensity}
                  onChange={() => setPainIntensity(intensity)}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  {intensity}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box id="q-10" sx={{ mb: 3 }}>
          <FormRadioGroup
            label="Under pågående anfall, förvärras då smärtan av normal fysisk aktivitet, som t.ex gång i trappor?"
            name="physicalActivityEffect"
            value={physicalActivityEffect}
            options={[
              { label: 'Ja', value: 'yes' },
              { label: 'Nej', value: 'no' },
            ]}
            onChange={(val) => setPhysicalActivityEffect(val)}
            onBlur={handleAutoSave}
          />
        </Box>

        <Box id="q-11" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Om du har fria möjligheter, vad gör du då helst under ett anfall?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {[
              'Lägger mig ned i ett mörkt och tyst rum',
              'Tar en promenad',
              'Sätter mig ned och tar det lugnt',
              'Går omkring då smärtan är svår att stå ut med eller förvärras i liggande',
            ].map((action) => (
              <Box
                component="label"
                key={action}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  onBlur={handleAutoSave}
                  name="preferredActionDuringAttack"
                  value={action}
                  checked={preferredActionDuringAttack === action}
                  onChange={() => setPreferredActionDuringAttack(action)}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  {action}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box id="q-12" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Har du andra symptom utöver huvudvärk under ett anfall?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {[
              'illamående/kräkningar',
              'överkänslighet för ljus',
              'överkänslighet för ljud',
              'överkänslighet för lukter',
              'Nej, inget av ovanstående',
            ].map((symptom) => (
              <Box
                component="label"
                key={symptom}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  onBlur={handleAutoSave}
                  value={symptom}
                  checked={associatedSymptoms.includes(symptom)}
                  onChange={(e) => {
                    const { value } = e.target;
                    setAssociatedSymptoms((prev) => {
                      const isChecked = prev.includes(value);

                      if (value === 'Nej, inget av ovanstående') {
                        return isChecked ? [] : [value];
                      }

                      const filtered = prev.filter(
                        (item) => item !== 'Nej, inget av ovanstående'
                      );

                      if (isChecked) {
                        return filtered.filter((item) => item !== value);
                      }
                      return [...filtered, value];
                    });
                  }}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  {symptom}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box id="q-13" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Har du något eller några av följande symptom strax innan eller under
            en huvudvärksattack?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {[
              'synstörning (flimmer/blixtar i delar av synfältet)',
              'känselstörning (domning/stickning i ena sidans arm och/eller ben)',
              'svårighet att hitta ord',
              'sluddrigt tal',
              'svaghet i ena sidans arm och/eller ben',
              'yrsel',
              'hängande ögonlock och/eller mindre pupill på samma sida som huvudvärken',
              'svullet ögonlock på samma sida som huvudvärken',
              'ögonrodnad och/eller ökat tårflöde på samma sida som huvudvärken',
              'rinnande näsa eller nästäppa på samma sida som huvudvärken',
              'svettning i pannan och ansiktet på samma sida som huvudvärken',
              'Inget av ovanstående',
            ].map((aura) => (
              <Box
                component="label"
                key={aura}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  onBlur={handleAutoSave}
                  value={aura}
                  checked={auraSymptoms.includes(aura)}
                  onChange={(e) => {
                    const { value } = e.target;
                    const next = toggleExclusive(
                      auraSymptoms,
                      value,
                      'Inget av ovanstående'
                    );
                    setAuraSymptoms(next);
                    if (!next.some((item) => item !== 'Inget av ovanstående')) {
                      setDizzinessType('');
                      setDizzinessDuration('');
                    }
                  }}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  {aura}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {auraSymptoms.includes('yrsel') && (
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Hur skulle du beskriva din yrsel?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                'snurrande som när man åker karusell',
                'gungande som på en båt',
                'allmän olustkänsla',
                'inget av ovanstående',
              ].map((dizziness) => (
                <Box
                  component="label"
                  key={dizziness}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    onBlur={handleAutoSave}
                    name="dizzinessType"
                    value={dizziness}
                    checked={dizzinessType === dizziness}
                    onChange={() => setDizzinessType(dizziness)}
                    style={{ marginRight: '8px' }}
                  />
                  <Typography
                    sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                  >
                    {dizziness}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {auraSymptoms.some((item) => item !== 'Inget av ovanstående') && (
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Hur länge varar vart och ett av dessa symptom?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                'mindre än 5 min',
                '5-15 min',
                '15-60 min',
                '1-4 timmar',
                'mer än 4 timmar',
              ].map((duration) => (
                <Box
                  component="label"
                  key={duration}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    onBlur={handleAutoSave}
                    name="dizzinessDuration"
                    value={duration}
                    checked={dizzinessDuration === duration}
                    onChange={() => setDizzinessDuration(duration)}
                    style={{ marginRight: '8px' }}
                  />
                  <Typography
                    sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                  >
                    {duration}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Box id="q-16" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Ungefär hur ofta har du ovan beskrivna huvudvärk?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {[
              'mindre än en gång per år',
              '1-11 gånger per år',
              '1-3 ggr per månad',
              '4-7 ggr per månad',
              '8-14 ggr per månad',
              '15-29 ggr per månad',
              'dagligen',
            ].map((frequency) => (
              <Box
                component="label"
                key={frequency}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  onBlur={handleAutoSave}
                  name="headacheFrequency"
                  value={frequency}
                  checked={headacheFrequency === frequency}
                  onChange={() => setHeadacheFrequency(frequency)}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  {frequency}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box id="q-17" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Sedan hur länge har du haft denna huvudvärksfrekvens?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {[
              'mindre än 1 månad',
              '1-3 månader',
              '3-6 månader',
              '6-12 månader',
              '1-2 år',
              '2-5 år',
              '5-10 år',
              '10-20 år',
              'mer än 20 år',
              'sedan debuten',
            ].map((duration) => (
              <Box
                component="label"
                key={duration}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  onBlur={handleAutoSave}
                  name="frequencyDuration"
                  value={duration}
                  checked={frequencyDuration === duration}
                  onChange={() => setFrequencyDuration(duration)}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  {duration}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box id="q-18" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Hur ofta hade du denna typ av huvudvärk de första åren efter
            debuten?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {[
              'mindre än en gång per år',
              '1-11 gånger per år',
              '1-3 ggr per månad',
              '4-7 ggr per månad',
              '8-14 ggr per månad',
              '15-29 ggr per månad',
              'dagligen',
            ].map((frequency) => (
              <Box
                component="label"
                key={frequency}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  onBlur={handleAutoSave}
                  name="initialFrequency"
                  value={frequency}
                  checked={initialFrequency === frequency}
                  onChange={() => setInitialFrequency(frequency)}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  {frequency}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box id="q-19" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Har huvudvärken försämrats med tiden?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="worseningOverTime"
                value="yes"
                checked={worseningOverTime === 'yes'}
                onChange={() => setWorseningOverTime('yes')}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Ja
              </Typography>
            </Box>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="worseningOverTime"
                value="no"
                checked={worseningOverTime === 'no'}
                onChange={() => setWorseningOverTime('no')}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Nej
              </Typography>
            </Box>
          </Box>
          {worseningOverTime === 'yes' && (
            <Box sx={{ mt: 1 }}>
              <Typography
                sx={{
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Hur länge är det sedan din huvudvärk försämrades?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {[
                  'mindre än 1 månad',
                  '1-3 månader',
                  '3-6 månader',
                  '6-12 månader',
                  '1-2 år',
                  '2-5 år',
                  '5-10 år',
                  '10-20 år',
                  'mer än 20 år',
                ].map((duration) => (
                  <Box
                    component="label"
                    key={duration}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      onBlur={handleAutoSave}
                      name="worseningDuration"
                      value={duration}
                      checked={worseningDuration === duration}
                      onChange={() => setWorseningDuration(duration)}
                      style={{ marginRight: '8px' }}
                    />
                    <Typography
                      sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                    >
                      {duration}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <Box id="q-20" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Triggas din huvudvärk av någon eller några följande faktorer?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {[
              'menstruation och/eller ägglossning',
              'födoämne (t ex choklad, starka ostar, citrusfrukter)',
              'alkohol',
              'stress',
              'solljus',
              'starka lukter',
              'träning',
              'inget av ovanstående',
            ].map((trigger) => (
              <Box
                component="label"
                key={trigger}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  onBlur={handleAutoSave}
                  value={trigger}
                  checked={triggers.includes(trigger)}
                  onChange={(e) => {
                    const { value } = e.target;
                    setTriggers((prev) =>
                      toggleExclusive(prev, value, 'inget av ovanstående')
                    );
                  }}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  {trigger}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        {hasMultipleHeadaches === 'yes' && (
          <Box sx={{ mb: 3 }}>
            <HasMultipleHeadaches
              additionalHeadaches={additionalHeadaches}
              setAdditionalHeadaches={setAdditionalHeadaches}
              handleFormSubmit={handleAutoSave}
            />

            <Typography
              sx={{
                color: '#374151',
                fontSize: '14px',
                mt: 1,
                fontFamily: DS.fontBody,
              }}
            >
              (samma huvudvärkskaraktär men med olika smärtintensitet räknas som
              en slags huvudvärk)
            </Typography>
          </Box>
        )}

        <Box id="q-21" sx={{ mb: 3 }}>
          <FormTextInput
            label="Om du räknar in all form av huvudvärk, hur många dagar med huvudvärk uppskattar du att du har per månad?"
            type="number"
            inputMode="numeric"
            min={0}
            max={31}
            value={headacheDaysPerMonth}
            onChange={(e) => {
              const input = e.target;
              const { value, validity } = input;
              if (validity.badInput) {
                input.value = headacheDaysPerMonth;
                return;
              }
              if (value !== '' && !/^\d+$/.test(value)) return;
              if (Number(value) > 31) return;
              setHeadacheDaysPerMonth(value);
            }}
            onBlur={handleAutoSave}
          />
        </Box>

        <Box id="q-22" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Lider du av något/några av nedanföljande tillstånd?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {[
              'nedstämdhet',
              'ångest',
              'sömnrubbning',
              'bruxism (tandgnissling)',
              'rikliga snarkningar nattetid',
              'nej, inget av ovanstående',
            ].map((comorbidity) => (
              <Box
                component="label"
                key={comorbidity}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  onBlur={handleAutoSave}
                  value={comorbidity}
                  checked={
                    Array.isArray(comorbidities) &&
                    comorbidities.includes(comorbidity)
                  }
                  onChange={(e) => {
                    const { value } = e.target;
                    setComorbidities((prev) => {
                      const isChecked = prev.includes(value);

                      if (value === 'nej, inget av ovanstående') {
                        return isChecked ? [] : [value];
                      }

                      const filtered = prev.filter(
                        (item) => item !== 'nej, inget av ovanstående'
                      );

                      if (isChecked) {
                        return filtered.filter((item) => item !== value);
                      }
                      return [...filtered, value];
                    });
                  }}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  {comorbidity}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box id="q-23" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Vilka icke medikamentella behandlingar har du provat?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {[
              'massage',
              'naprapat/kiropraktor behandling',
              'akupunktur',
              'TENS',
              'yoga eller annan avslappningsmetod',
              'bettskena',
              'inga av ovanstående',
            ].map((treatment) => (
              <Box
                component="label"
                key={treatment}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  onBlur={handleAutoSave}
                  value={treatment}
                  checked={nonMedicationTreatments.includes(treatment)}
                  onChange={(e) => {
                    const { value } = e.target;

                    setNonMedicationTreatments((prev) => {
                      let updated: string[];

                      if (prev.includes(value)) {
                        updated = prev.filter((item) => item !== value);

                        switch (value) {
                          case 'massage':
                            setMassageEffect('');
                            break;
                          case 'naprapat/kiropraktor behandling':
                            setNaprapatEffect('');
                            break;
                          case 'akupunktur':
                            setAcupunctureEffect('');
                            break;
                          case 'TENS':
                            setTensEffect('');
                            break;
                          case 'yoga eller annan avslappningsmetod':
                            setYogaEffect('');
                            break;
                          case 'bettskena':
                            setBettskenEffect('');
                            break;
                          default:
                            break;
                        }
                      } else {
                        updated =
                          value === 'inga av ovanstående'
                            ? ['inga av ovanstående']
                            : prev
                                .filter((v) => v !== 'inga av ovanstående')
                                .concat(value);

                        if (value === 'inga av ovanstående') {
                          setMassageEffect('');
                          setNaprapatEffect('');
                          setAcupunctureEffect('');
                          setTensEffect('');
                          setYogaEffect('');
                          setBettskenEffect('');
                        }
                      }

                      return updated;
                    });
                  }}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  {treatment}
                </Typography>
              </Box>
            ))}
          </Box>
          {nonMedicationTreatments.includes('massage') && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Vilken effekt hade massage på din huvudvärk?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                {['Mycket god', 'God', 'Måttlig', 'Tveksam', 'Ingen'].map(
                  (effect) => (
                    <Box
                      component="label"
                      key={effect}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        onBlur={handleAutoSave}
                        name="massageEffect"
                        value={effect}
                        checked={massageEffect === effect}
                        onChange={() => setMassageEffect(effect)}
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                      >
                        {effect}
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
            </Box>
          )}
          {nonMedicationTreatments.includes(
            'naprapat/kiropraktor behandling'
          ) && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Vilken effekt hade naprapat/kiropraktor behandling på din
                huvudvärk?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                {['Mycket god', 'God', 'Måttlig', 'Tveksam', 'Ingen'].map(
                  (effect) => (
                    <Box
                      component="label"
                      key={effect}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        onBlur={handleAutoSave}
                        name="naprapatEffect"
                        value={effect}
                        checked={naprapatEffect === effect}
                        onChange={() => setNaprapatEffect(effect)}
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                      >
                        {effect}
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
            </Box>
          )}
          {nonMedicationTreatments.includes('akupunktur') && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Vilken effekt hade akupunktur på din huvudvärk?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                {['Mycket god', 'God', 'Måttlig', 'Tveksam', 'Ingen'].map(
                  (effect) => (
                    <Box
                      component="label"
                      key={effect}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        onBlur={handleAutoSave}
                        name="acupunctureEffect"
                        value={effect}
                        checked={acupunctureEffect === effect}
                        onChange={() => setAcupunctureEffect(effect)}
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                      >
                        {effect}
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
            </Box>
          )}
          {nonMedicationTreatments.includes('TENS') && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Vilken effekt hade TENS på din huvudvärk?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                {['Mycket god', 'God', 'Måttlig', 'Tveksam', 'Ingen'].map(
                  (effect) => (
                    <Box
                      component="label"
                      key={effect}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        onBlur={handleAutoSave}
                        name="tensEffect"
                        value={effect}
                        checked={tensEffect === effect}
                        onChange={() => setTensEffect(effect)}
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                      >
                        {effect}
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
            </Box>
          )}
          {nonMedicationTreatments.includes(
            'yoga eller annan avslappningsmetod'
          ) && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Vilken effekt hade yoga eller annan avslappningsmetod på din
                huvudvärk?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                {['Mycket god', 'God', 'Måttlig', 'Tveksam', 'Ingen'].map(
                  (effect) => (
                    <Box
                      component="label"
                      key={effect}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        onBlur={handleAutoSave}
                        name="yogaEffect"
                        value={effect}
                        checked={yogaEffect === effect}
                        onChange={() => setYogaEffect(effect)}
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                      >
                        {effect}
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
            </Box>
          )}
          {nonMedicationTreatments.includes('bettskena') && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Vilken effekt hade bettskena på din huvudvärk?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                {['Mycket god', 'God', 'Måttlig', 'Tveksam', 'Ingen'].map(
                  (effect) => (
                    <Box
                      component="label"
                      key={effect}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        onBlur={handleAutoSave}
                        name="bettskenEffect"
                        value={effect}
                        checked={bettskenEffect === effect}
                        onChange={() => setBettskenEffect(effect)}
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                      >
                        {effect}
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
            </Box>
          )}
        </Box>

        <Box id="q-24" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Bedömer du själv eller har någon i sjukvården ansett att du har
            överkonsumtion av smärtstillande läkemedel?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="medicationOveruse"
                value="yes"
                checked={medicationOveruse === 'yes'}
                onChange={() => setMedicationOveruse('yes')}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Ja
              </Typography>
            </Box>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="medicationOveruse"
                value="no"
                checked={medicationOveruse === 'no'}
                onChange={() => setMedicationOveruse('no')}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Nej
              </Typography>
            </Box>
          </Box>
          {medicationOveruse === 'yes' && (
            <Box sx={{ mt: 1 }}>
              <input
                type="text"
                onBlur={handleAutoSave}
                placeholder="Vilket preparat?"
                value={overusedMedication || ''}
                onChange={(e) => setOverusedMedication(e.target.value)}
                style={inputStyle}
              />
              <Typography
                sx={{
                  color: '#374151',
                  mt: 2,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Sedan hur länge?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {[
                  'mindre än 1 månad',
                  '1-3 månader',
                  '3-6 månader',
                  '6-12 månader',
                  '1-2 år',
                  '2-5 år',
                  '5-10 år',
                  '10-20 år',
                  'mer än 20 år',
                  'sedan debuten',
                ].map((duration) => (
                  <Box
                    component="label"
                    key={duration}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      onBlur={handleAutoSave}
                      name="overuseDuration"
                      value={duration}
                      checked={overuseDuration === duration}
                      onChange={() => setOveruseDuration(duration)}
                      style={{ marginRight: '8px' }}
                    />
                    <Typography
                      sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                    >
                      {duration}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <Box id="q-25" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Har du genomgått läkemedelsavgiftning?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="hasWithdrawal"
                value="yes"
                checked={hasWithdrawal === 'yes'}
                onChange={() => setHasWithdrawal('yes')}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Ja
              </Typography>
            </Box>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="hasWithdrawal"
                value="no"
                checked={hasWithdrawal === 'no'}
                onChange={() => setHasWithdrawal('no')}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Nej
              </Typography>
            </Box>
          </Box>

          {hasWithdrawal === 'yes' && (
            <>
              <Box sx={{ mt: 2 }}>
                <Typography
                  sx={{
                    color: '#374151',
                    fontFamily: DS.fontBody,
                    fontSize: '16px',
                  }}
                >
                  När genomgick du avgiftning? (datum - ÅÅÅÅ eller ÅÅÅÅMM)
                </Typography>
                <input
                  type="text"
                  onBlur={handleAutoSave}
                  value={withdrawalDate || ''}
                  onChange={(e) => setWithdrawalDate(e.target.value)}
                  style={inputStyle}
                />
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography
                  sx={{
                    color: '#374151',
                    fontFamily: DS.fontBody,
                    fontSize: '16px',
                  }}
                >
                  Vad hade avgiftningen för effekt?
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                  {[
                    'Klart minskad huvudvärk',
                    'Måttligt minskad huvudvärk',
                    'En aning minskad huvudvärk',
                    'Ingen skillnad',
                  ].map((effect) => (
                    <Box
                      component="label"
                      key={effect}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        onBlur={handleAutoSave}
                        name="withdrawalEffect"
                        value={effect}
                        checked={withdrawalEffect === effect}
                        onChange={() => setWithdrawalEffect(effect)}
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                      >
                        {effect}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}
        </Box>

        <Box id="q-26" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Har du genomgått skiktröntgen (datortomografi, CT, DT) av huvudet?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="radiologyExam"
                value="yes"
                checked={radiologyExam === 'yes'}
                onChange={() => setRadiologyExam('yes')}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Ja
              </Typography>
            </Box>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="radiologyExam"
                value="no"
                checked={radiologyExam === 'no'}
                onChange={() => {
                  setRadiologyExam('no');
                  setRadiologyDate('');
                  setRadiologyLocation('');
                  setRadiologyResult('');
                  setDescribeAnomaly('');
                }}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Nej
              </Typography>
            </Box>
          </Box>
          {radiologyExam === 'yes' && (
            <Box sx={{ mt: 1 }}>
              <Typography
                sx={{
                  color: '#374151',
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                När gjordes undersökningen? (datum)
              </Typography>
              <input
                type="text"
                onBlur={handleAutoSave}
                value={radiologyDate}
                onChange={(e) => setRadiologyDate(e.target.value)}
                style={inputStyle}
              />
              <Typography
                sx={{
                  color: '#374151',
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Var gjordes undersökningen?
              </Typography>
              <input
                type="text"
                onBlur={handleAutoSave}
                value={radiologyLocation}
                onChange={(e) => setRadiologyLocation(e.target.value)}
                style={inputStyle}
              />
              <Typography
                sx={{
                  color: '#374151',
                  mt: 2,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Vad visade undersökningen?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {[
                  'normalt resultat',
                  'väsentligen normalt med lättare/obetydlig anmärkning',
                  'onormalt fynd',
                ].map((result) => (
                  <Box
                    component="label"
                    key={result}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      onBlur={handleAutoSave}
                      name="radiologyResult"
                      value={result}
                      checked={radiologyResult === result}
                      onChange={() => setRadiologyResult(result)}
                      style={{ marginRight: '8px' }}
                    />
                    <Typography
                      sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                    >
                      {result}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {radiologyResult === 'onormalt fynd' && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      color: '#374151',
                      mb: 1,
                      fontFamily: DS.fontBody,
                      fontSize: '16px',
                    }}
                  >
                    Om du vet, beskriv vad man hittade för avvikande fynd på
                    undersökningen:
                  </Typography>
                  <input
                    type="text"
                    onBlur={handleAutoSave}
                    value={describeAnomaly || ''}
                    onChange={(e) => setDescribeAnomaly(e.target.value)}
                    style={inputStyle}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box id="q-27" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Har du genomgått magnetkameraundersökning (MRT) av huvudet?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="controllMrt"
                value="yes"
                checked={controllMrt === 'yes'}
                onChange={() => setControllMrt('yes')}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Ja
              </Typography>
            </Box>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="controllMrt"
                value="no"
                checked={controllMrt === 'no'}
                onChange={() => {
                  setControllMrt('no');
                  setMrtDate('');
                  setMrtLocation('');
                  setMrtResult('');
                  setAdditionalRadiologyInfo('');
                }}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Nej
              </Typography>
            </Box>
          </Box>
          {controllMrt === 'yes' && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      color: '#374151',
                      fontFamily: DS.fontBody,
                      fontSize: '16px',
                    }}
                  >
                    När gjordes undersökningen? (datum)
                  </Typography>
                  <input
                    type="text"
                    onBlur={handleAutoSave}
                    value={mrtDate || ''}
                    onChange={(e) => setMrtDate(e.target.value)}
                    style={inputStyle}
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      color: '#374151',
                      fontFamily: DS.fontBody,
                      fontSize: '16px',
                    }}
                  >
                    Var gjordes undersökningen?
                  </Typography>
                  <input
                    type="text"
                    onBlur={handleAutoSave}
                    value={mrtLocation || ''}
                    onChange={(e) => setMrtLocation(e.target.value)}
                    style={inputStyle}
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      color: '#374151',
                      fontFamily: DS.fontBody,
                      fontSize: '16px',
                    }}
                  >
                    Vad visade undersökningen?
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    {[
                      'normalt resultat',
                      'väsentligen normalt med lättare/obetydlig anmärkning',
                      'onormalt fynd',
                    ].map((result) => (
                      <Box
                        component="label"
                        key={result}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="radio"
                          onBlur={handleAutoSave}
                          name="mrtResult"
                          value={result}
                          checked={mrtResult === result}
                          onChange={() => setMrtResult(result)}
                          style={{ marginRight: '8px' }}
                        />
                        <Typography
                          sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                        >
                          {result}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
              {mrtResult === 'onormalt fynd' && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      color: '#374151',
                      mb: 1,
                      fontFamily: DS.fontBody,
                      fontSize: '16px',
                    }}
                  >
                    Om du vet, beskriv vad man hittade för avvikande fynd på
                    undersökningen:
                  </Typography>
                  <input
                    type="text"
                    onBlur={handleAutoSave}
                    value={additionalRadiologyInfo || ''}
                    onChange={(e) => setAdditionalRadiologyInfo(e.target.value)}
                    style={inputStyle}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box id="q-28" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Tar du något smärtlindrande läkemedel mot din huvudvärk?
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box
                component="label"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  onBlur={handleAutoSave}
                  name="takesPainMedication"
                  value="yes"
                  checked={takesPainMedication === 'yes'}
                  onChange={() => setTakesPainMedication('yes')}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  Ja
                </Typography>
              </Box>
              <Box
                component="label"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  onBlur={handleAutoSave}
                  name="takesPainMedication"
                  value="no"
                  checked={takesPainMedication === 'no'}
                  onChange={() => setTakesPainMedication('no')}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  Nej
                </Typography>
              </Box>
            </Box>
            {takesPainMedication === 'yes' && (
              <>
                <Box>
                  <Typography
                    sx={{
                      color: '#374151',
                      mb: 1,
                      fontFamily: DS.fontBody,
                      fontSize: '16px',
                    }}
                  >
                    Vilket (läkemedelsnamn)?
                  </Typography>
                  <input
                    type="text"
                    onBlur={handleAutoSave}
                    value={painMedicationName || ''}
                    onChange={(e) => setPainMedicationName(e.target.value)}
                    style={{ ...inputStyle, marginTop: 0 }}
                  />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      color: '#374151',
                      mb: 1,
                      fontFamily: DS.fontBody,
                      fontSize: '16px',
                    }}
                  >
                    Vilken dos (styrka/ behandlingstillfälle)?
                  </Typography>
                  <input
                    type="text"
                    onBlur={handleAutoSave}
                    value={painMedicationDose || ''}
                    onChange={(e) => setPainMedicationDose(e.target.value)}
                    style={{ ...inputStyle, marginTop: 0 }}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      color: '#374151',
                      mb: 1,
                      fontFamily: DS.fontBody,
                      fontSize: '16px',
                    }}
                  >
                    Vilken symptomlindrande effekt har detta läkemedel?
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {['Mycket god', 'God', 'Måttlig', 'Sparsam', 'Ingen'].map(
                      (effect) => (
                        <Box
                          component="label"
                          key={effect}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="radio"
                            onBlur={handleAutoSave}
                            name="painMedicationEffect"
                            value={effect}
                            checked={painMedicationEffect === effect}
                            onChange={() => setPainMedicationEffect(effect)}
                            style={{ marginRight: '8px' }}
                          />
                          <Typography
                            sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                          >
                            {effect}
                          </Typography>
                        </Box>
                      )
                    )}
                  </Box>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      color: '#374151',
                      mb: 1,
                      fontFamily: DS.fontBody,
                      fontSize: '16px',
                    }}
                  >
                    Hur ofta hjälper detta läkemedel?
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {[
                      'Alltid',
                      'För det mesta',
                      'Ibland',
                      'Sällan',
                      'Aldrig',
                    ].map((effectiveness) => (
                      <Box
                        component="label"
                        key={effectiveness}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="radio"
                          onBlur={handleAutoSave}
                          name="painMedicationEffectiveness"
                          value={effectiveness}
                          checked={
                            painMedicationEffectiveness === effectiveness
                          }
                          onChange={(e) =>
                            setPainMedicationEffectiveness(e.target.value)
                          }
                          style={{ marginRight: '8px' }}
                        />
                        <Typography
                          sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                        >
                          {effectiveness}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      color: '#374151',
                      mb: 1,
                      fontFamily: DS.fontBody,
                      fontSize: '16px',
                    }}
                  >
                    Hur ofta tar du detta läkemedel?
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {[
                      'mindre än 1 dag i veckan',
                      '1-2 dagar i veckan',
                      '3-5 dagar i veckan',
                      'mer än 5 dagar i veckan',
                    ].map((frequency) => (
                      <Box
                        component="label"
                        key={frequency}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="radio"
                          onBlur={handleAutoSave}
                          name="painMedicationFrequency"
                          value={frequency}
                          checked={painMedicationFrequency === frequency}
                          onChange={() => setPainMedicationFrequency(frequency)}
                          style={{ marginRight: '8px' }}
                        />
                        <Typography
                          sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                        >
                          {frequency}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: '#374151',
                      fontFamily: DS.fontBody,
                      fontSize: '16px',
                    }}
                  >
                    Använder du någon annan anfallsmedicin än den du just uppgav
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box
                      component="label"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        onBlur={handleAutoSave}
                        name="hasAdditionalAttackMedication"
                        value="yes"
                        checked={hasAdditionalAttackMedication === 'yes'}
                        onChange={() => {
                          setHasAdditionalAttackMedication('yes');
                          if (additionalAttackMedications.length === 0) {
                            addNewAttackMedication();
                          }
                        }}
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                      >
                        Ja
                      </Typography>
                    </Box>
                    <Box
                      component="label"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        onBlur={handleAutoSave}
                        name="hasAdditionalAttackMedication"
                        value="no"
                        checked={hasAdditionalAttackMedication === 'no'}
                        onChange={() => {
                          setHasAdditionalAttackMedication('no');
                          setAdditionalAttackMedications([]);
                        }}
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                      >
                        Nej
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {hasAdditionalAttackMedication === 'yes' && (
                  <HasAdditionalAttackMedication
                    additionalAttackMedications={additionalAttackMedications}
                    setAdditionalAttackMedications={
                      setAdditionalAttackMedications
                    }
                    handleFormSubmit={handleAutoSave}
                  />
                )}

                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: '#374151',
                      mb: 1,
                      fontFamily: DS.fontBody,
                      fontSize: '16px',
                    }}
                  >
                    Har du tidigare provat något smärtlindrande läkemedel mot
                    huvudvärken?
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box
                      component="label"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        onBlur={handleAutoSave}
                        name="hasPreviousPainMedication"
                        value="yes"
                        checked={hasPreviousPainMedication === 'yes'}
                        onChange={() => setHasPreviousPainMedication('yes')}
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                      >
                        Ja
                      </Typography>
                    </Box>
                    <Box
                      component="label"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        onBlur={handleAutoSave}
                        name="hasPreviousPainMedication"
                        value="no"
                        checked={hasPreviousPainMedication === 'no'}
                        onChange={() => setHasPreviousPainMedication('no')}
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                      >
                        Nej
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {hasPreviousPainMedication === 'yes' && (
                  <>
                    <Box>
                      <Typography
                        sx={{
                          color: '#374151',
                          mb: 1,
                          fontFamily: DS.fontBody,
                          fontSize: '16px',
                        }}
                      >
                        Vilket läkemedel?
                      </Typography>
                      <input
                        type="text"
                        onBlur={handleAutoSave}
                        value={previousMedicationName || ''}
                        onChange={(e) =>
                          setPreviousMedicationName(e.target.value)
                        }
                        style={{ ...inputStyle, marginTop: 0 }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          color: '#374151',
                          mb: 1,
                          fontFamily: DS.fontBody,
                          fontSize: '16px',
                        }}
                      >
                        Vilken dos?
                      </Typography>
                      <input
                        type="text"
                        onBlur={handleAutoSave}
                        value={previousMedicationDose || ''}
                        onChange={(e) =>
                          setPreviousMedicationDose(e.target.value)
                        }
                        style={{ ...inputStyle, marginTop: 0 }}
                      />
                    </Box>

                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#374151',
                            mb: 1,
                            fontFamily: DS.fontBody,
                            fontSize: '16px',
                          }}
                        >
                          Hur ofta tog du medicinen?
                        </Typography>
                        {[
                          'mindre än 1 dag i veckan',
                          '1-2 dagar i veckan',
                          '3-5 dagar i veckan',
                          'mer än 5 dagar i veckan',
                        ].map((freq) => (
                          <Box
                            component="label"
                            key={freq}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                            }}
                          >
                            <input
                              type="radio"
                              onBlur={handleAutoSave}
                              name="previousMedicationFrequency"
                              value={freq}
                              checked={previousMedicationFrequency === freq}
                              onChange={(e) =>
                                setPreviousMedicationFrequency(e.target.value)
                              }
                              style={{ marginRight: '8px' }}
                            />
                            <Typography
                              sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                            >
                              {freq}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          color: '#374151',
                          mb: 1,
                          fontFamily: DS.fontBody,
                          fontSize: '16px',
                        }}
                      >
                        Startår:
                      </Typography>
                      <input
                        type="text"
                        onBlur={handleAutoSave}
                        value={previousMedicationStartYear || ''}
                        onChange={(e) =>
                          setPreviousMedicationStartYear(e.target.value)
                        }
                        style={{ ...inputStyle, marginTop: 0 }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          color: '#374151',
                          mb: 1,
                          fontFamily: DS.fontBody,
                          fontSize: '16px',
                        }}
                      >
                        Varför slutade du med medicinen?
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        {['Bristande effekt', 'Biverkan', 'Annat skäl'].map(
                          (reason) => (
                            <Box
                              component="label"
                              key={reason}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                              }}
                            >
                              <input
                                type="radio"
                                onBlur={handleAutoSave}
                                name="previousMedicationStopReason"
                                value={reason}
                                checked={
                                  previousMedicationStopReason === reason
                                }
                                onChange={(e) =>
                                  setPreviousMedicationStopReason(
                                    e.target.value
                                  )
                                }
                                style={{ marginRight: '8px' }}
                              />
                              <Typography
                                sx={{
                                  fontSize: '14px',
                                  fontFamily: DS.fontBody,
                                }}
                              >
                                {reason}
                              </Typography>
                            </Box>
                          )
                        )}
                      </Box>
                    </Box>
                    {previousMedicationStopReason === 'Bristande effekt' && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          sx={{
                            color: '#374151',
                            mb: 1,
                            fontFamily: DS.fontBody,
                            fontSize: '16px',
                          }}
                        >
                          Stoppår:
                        </Typography>
                        <input
                          type="text"
                          onBlur={handleAutoSave}
                          value={previousMedicationStopYear || ''}
                          onChange={(e) =>
                            setPreviousMedicationStopYear(e.target.value)
                          }
                          style={{ ...inputStyle, marginTop: 0 }}
                        />
                      </Box>
                    )}
                    {previousMedicationStopReason === 'Biverkan' && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          sx={{
                            color: '#374151',
                            mb: 1,
                            fontFamily: DS.fontBody,
                            fontSize: '16px',
                          }}
                        >
                          Ange biverkan: (max 10 ord)
                        </Typography>
                        <input
                          type="text"
                          onBlur={handleAutoSave}
                          value={previousMedicationSideEffect}
                          onChange={(e) =>
                            setPreviousMedicationSideEffect(e.target.value)
                          }
                          style={{
                            ...inputStyle,
                            marginTop: 0,
                            marginBottom: '16px',
                          }}
                        />
                        <Typography
                          sx={{
                            color: '#374151',
                            mb: 1,
                            fontFamily: DS.fontBody,
                            fontSize: '16px',
                          }}
                        >
                          Stoppår:
                        </Typography>
                        <input
                          type="text"
                          onBlur={handleAutoSave}
                          value={previousMedicationStopYear || ''}
                          onChange={(e) =>
                            setPreviousMedicationStopYear(e.target.value)
                          }
                          style={{ ...inputStyle, marginTop: 0 }}
                        />
                      </Box>
                    )}
                    {previousMedicationStopReason === 'Annat skäl' && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          sx={{
                            color: '#374151',
                            mb: 1,
                            fontFamily: DS.fontBody,
                            fontSize: '16px',
                          }}
                        >
                          Ange skäl till avslut: (max 10 ord)
                        </Typography>
                        <input
                          type="text"
                          onBlur={handleAutoSave}
                          value={previousMedicationOtherReason}
                          onChange={(e) =>
                            setPreviousMedicationOtherReason(e.target.value)
                          }
                          style={{ ...inputStyle, marginTop: 0 }}
                        />
                        <Typography
                          sx={{
                            color: '#374151',
                            mb: 1,
                            fontFamily: DS.fontBody,
                            fontSize: '16px',
                          }}
                        >
                          Stoppår:
                        </Typography>
                        <input
                          type="text"
                          onBlur={handleAutoSave}
                          value={previousMedicationStopYear || ''}
                          onChange={(e) =>
                            setPreviousMedicationStopYear(e.target.value)
                          }
                          style={{ ...inputStyle, marginTop: 0 }}
                        />
                      </Box>
                    )}

                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: '#374151',
                        mt: 2,
                        fontFamily: DS.fontBody,
                        fontSize: '16px',
                      }}
                    >
                      Har du tidigare provat något annat smärtlindrande
                      läkemedel än det du just uppgav?
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box
                        component="label"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="radio"
                          onBlur={handleAutoSave}
                          name="hasOtherPreviousPainMedicationStop"
                          value="yes"
                          checked={hasOtherPreviousPainMedicationStop === 'yes'}
                          onChange={(e) => {
                            setHasOtherPreviousPainMedicationStop(
                              e.target.value
                            );
                            if (additionalPreviousMedications.length === 0) {
                              addNewPreviousMedication();
                            }
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        <Typography
                          sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                        >
                          Ja
                        </Typography>
                      </Box>
                      <Box
                        component="label"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="radio"
                          onBlur={handleAutoSave}
                          name="hasOtherPreviousPainMedicationStop"
                          value="no"
                          checked={hasOtherPreviousPainMedicationStop === 'no'}
                          onChange={(e) => {
                            setHasOtherPreviousPainMedicationStop(
                              e.target.value
                            );
                            setAdditionalPreviousMedications([]);
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        <Typography
                          sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                        >
                          Nej
                        </Typography>
                      </Box>
                    </Box>
                    {hasOtherPreviousPainMedicationStop === 'yes' && (
                      <HasAdditionalPreviousMedication
                        additionalPreviousMedications={
                          additionalPreviousMedications
                        }
                        setAdditionalPreviousMedications={
                          setAdditionalPreviousMedications
                        }
                        handleFormSubmit={handleAutoSave}
                      />
                    )}
                  </>
                )}
              </>
            )}

            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mt: 2,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Tar du något förebyggande läkemedel mot din huvudvärk?
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box
                component="label"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  onBlur={handleAutoSave}
                  name="preventiveMedicationHeadic"
                  value="yes"
                  checked={preventiveMedicationHeadic === 'yes'}
                  onChange={() => setPreventiveMedicationHeadic('yes')}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  Ja
                </Typography>
              </Box>
              <Box
                component="label"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  onBlur={handleAutoSave}
                  name="preventiveMedicationHeadic"
                  value="no"
                  checked={preventiveMedicationHeadic === 'no'}
                  onChange={() => setPreventiveMedicationHeadic('no')}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  Nej
                </Typography>
              </Box>
            </Box>

            {preventiveMedicationHeadic === 'yes' && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  sx={{
                    color: '#374151',
                    fontFamily: DS.fontBody,
                    fontSize: '16px',
                  }}
                >
                  Vilket läkemedel?
                </Typography>
                <input
                  type="text"
                  onBlur={handleAutoSave}
                  value={preventiveMedicationNameHeadic || ''}
                  onChange={(e) =>
                    setPreventiveMedicationNameHeadic(e.target.value)
                  }
                  style={inputStyle}
                />
                <Typography
                  sx={{
                    color: '#374151',
                    mt: 1,
                    fontFamily: DS.fontBody,
                    fontSize: '14px',
                  }}
                >
                  Vilket dos?
                </Typography>
                <input
                  type="text"
                  onBlur={handleAutoSave}
                  value={preventiveMedicationDoseHeadic || ''}
                  onChange={(e) =>
                    setPreventiveMedicationDoseHeadic(e.target.value)
                  }
                  style={inputStyle}
                />
                <Typography
                  sx={{
                    color: '#374151',
                    mt: 2,
                    mb: 1,
                    fontFamily: DS.fontBody,
                    fontSize: '14px',
                  }}
                >
                  Hur har detta läkemedel påverkat din anfallsfrekvens?
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {[
                    'Betydligt färre anfall',
                    'Något färre anfall',
                    'Ingen märkbar effekt',
                  ].map((effect) => (
                    <Box
                      component="label"
                      key={effect}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        onBlur={handleAutoSave}
                        name="preventiveMedicationEffectivenessHeadic"
                        value={effect}
                        checked={
                          preventiveMedicationEffectivenessHeadic === effect
                        }
                        onChange={() =>
                          setPreventiveMedicationEffectivenessHeadic(effect)
                        }
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                      >
                        {effect}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography
                  sx={{
                    color: '#374151',
                    mt: 2,
                    fontFamily: DS.fontBody,
                    fontSize: '16px',
                  }}
                >
                  Startår:
                </Typography>
                <input
                  type="text"
                  onBlur={handleAutoSave}
                  value={preventiveMedicationStartYearHeadic}
                  onChange={(e) =>
                    setPreventiveMedicationStartYearHeadic(e.target.value)
                  }
                  style={inputStyle}
                />
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: '#374151',
                    mb: 1,
                    mt: 2,
                    fontFamily: DS.fontBody,
                    fontSize: '14px',
                  }}
                >
                  Upplever du några biverkningar av detta läkemedel?
                </Typography>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box
                    component="label"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      onBlur={handleAutoSave}
                      name="preventiveMedicationSideEffectsHeadic"
                      value="yes"
                      checked={preventiveMedicationSideEffectsHeadic === 'yes'}
                      onChange={(e) =>
                        setPreventiveMedicationSideEffectsHeadic(e.target.value)
                      }
                      style={{ marginRight: '8px' }}
                    />
                    <Typography
                      sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                    >
                      Ja
                    </Typography>
                  </Box>
                  <Box
                    component="label"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      onBlur={handleAutoSave}
                      name="preventiveMedicationSideEffectsHeadic"
                      value="no"
                      checked={preventiveMedicationSideEffectsHeadic === 'no'}
                      onChange={(e) =>
                        setPreventiveMedicationSideEffectsHeadic(e.target.value)
                      }
                      style={{ marginRight: '8px' }}
                    />
                    <Typography
                      sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                    >
                      Nej
                    </Typography>
                  </Box>
                </Box>

                {preventiveMedicationSideEffectsHeadic === 'yes' && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      sx={{
                        color: '#374151',
                        mb: 1,
                        fontFamily: DS.fontBody,
                        fontSize: '16px',
                      }}
                    >
                      Ange biverkan: (max 10 ord)
                    </Typography>
                    <input
                      type="text"
                      onBlur={handleAutoSave}
                      value={preventiveMedicationSideEffectDetailsHeadic || ''}
                      onChange={(e) =>
                        setPreventiveMedicationSideEffectDetailsHeadic(
                          e.target.value
                        )
                      }
                      style={{ ...inputStyle, marginTop: 0 }}
                    />
                  </Box>
                )}

                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: '#374151',
                      mb: 1,
                      fontFamily: DS.fontBody,
                      fontSize: '16px',
                    }}
                  >
                    Behandlas du med något annat förebyggande läkemedel än det
                    du just uppgav?
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box
                      component="label"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        onBlur={handleAutoSave}
                        name="hasAdditionalPreventiveMedicationHeadic"
                        value="yes"
                        checked={
                          hasAdditionalPreventiveMedicationHeadic === 'yes'
                        }
                        onChange={(e) => {
                          setHasAdditionalPreventiveMedicationHeadache(
                            e.target.value
                          );
                          if (additionalPreventiveMedications.length === 0) {
                            addNewPreventiveMedication();
                          }
                        }}
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                      >
                        Ja
                      </Typography>
                    </Box>
                    <Box
                      component="label"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        onBlur={handleAutoSave}
                        name="hasAdditionalPreventiveMedicationHeadic"
                        value="no"
                        checked={
                          hasAdditionalPreventiveMedicationHeadic === 'no'
                        }
                        onChange={(e) => {
                          setHasAdditionalPreventiveMedicationHeadache(
                            e.target.value
                          );
                          setAdditionalPreventiveMedications([]);
                        }}
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                      >
                        Nej
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {hasAdditionalPreventiveMedicationHeadic === 'yes' && (
                  <HasAdditionalPreventiveMedicationHeadache
                    additionalPreventiveMedications={
                      additionalPreventiveMedications
                    }
                    setAdditionalPreventiveMedications={
                      setAdditionalPreventiveMedications
                    }
                    handleFormSubmit={handleAutoSave}
                  />
                )}
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Har du tidigare provat något förebyggande läkemedel?
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box
                  component="label"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    onBlur={handleAutoSave}
                    name="hasPreviousPreventiveMedication"
                    value="yes"
                    checked={hasPreviousPreventiveMedication === 'yes'}
                    onChange={(e) => {
                      setHasPreviousPreventiveMedication(e.target.value);

                      if (previousPreventiveMedications.length === 0) {
                        addNewPreviousPreventiveMedication();
                      }
                    }}
                    style={{ marginRight: '8px' }}
                  />
                  <Typography
                    sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                  >
                    Ja
                  </Typography>
                </Box>
                <Box
                  component="label"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    onBlur={handleAutoSave}
                    name="hasPreviousPreventiveMedication"
                    value="no"
                    checked={hasPreviousPreventiveMedication === 'no'}
                    onChange={(e) => {
                      setHasPreviousPreventiveMedication(e.target.value);
                      setPreviousPreventiveMedications([]);
                    }}
                    style={{ marginRight: '8px' }}
                  />
                  <Typography
                    sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                  >
                    Nej
                  </Typography>
                </Box>
              </Box>
            </Box>

            {hasPreviousPreventiveMedication === 'yes' && (
              <MultiplePreviousPreventiveMedications
                previousPreventiveMedications={previousPreventiveMedications}
                setPreviousPreventiveMedications={
                  setPreviousPreventiveMedications
                }
                handleFormSubmit={handleAutoSave}
              />
            )}

            <Box>
              <Box
                sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: '#374151',
                    mb: 1,
                    fontFamily: DS.fontBody,
                    fontSize: '16px',
                  }}
                >
                  Tar du andra läkemedel förutom de du angett?
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {['Ja', 'Nej'].map((reason) => (
                    <Box
                      component="label"
                      key={reason}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        onBlur={handleAutoSave}
                        name="otherPreventiveMedication"
                        value={reason}
                        checked={otherPreventiveMedication === reason}
                        onChange={(e) => {
                          setOtherPreventiveMedication(e.target.value);

                          if (e.target.value === 'Ja') {
                            if (otherMedications.length === 0) {
                              addNewOtherMedication();
                            }
                          } else {
                            setOtherMedications([]);
                          }
                        }}
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                      >
                        {reason}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {otherPreventiveMedication === 'Ja' && (
                <MultipleOtherMedications
                  otherMedications={otherMedications}
                  setOtherMedications={setOtherMedications}
                  handleFormSubmit={handleAutoSave}
                />
              )}

              <Box
                sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: '#374151',
                    mb: 1,
                    fontFamily: DS.fontBody,
                    fontSize: '16px',
                  }}
                >
                  Är du allergisk eller överkänslig mot något läkemedel?
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {['Ja', 'Nej'].map((reason) => (
                    <Box
                      component="label"
                      key={reason}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        onBlur={handleAutoSave}
                        name="otherPreventiveMedicationOtherNo"
                        value={reason}
                        checked={otherPreventiveMedicationOtherNo === reason}
                        onChange={(e) => {
                          const { value } = e.target;
                          setOtherPreventiveMedicationOtherNo(value);

                          if (value === 'Ja') {
                            if (allergies.length === 0) {
                              setAllergies([{ allergen: '', reaction: '' }]);
                            }
                          } else {
                            setAllergies([]);
                          }
                        }}
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                      >
                        {reason}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {otherPreventiveMedicationOtherNo === 'Ja' && (
                <MultipleAllergies
                  allergies={allergies}
                  setAllergies={setAllergies}
                  handleFormSubmit={handleAutoSave}
                />
              )}
            </Box>
          </Box>
        </Box>

        <Box id="q-29" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Har någon eller några i din närmaste släkt migrän?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {[
              'far',
              'mor',
              'syskon',
              'far- eller morförälder',
              'faster/moster/farbror/morbror',
              'kusin',
              'nej',
            ].map((relative) => (
              <Box
                component="label"
                key={relative}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  onBlur={handleAutoSave}
                  value={relative}
                  checked={heredityMigraine.includes(relative)}
                  onChange={(e) => {
                    const { value } = e.target;
                    setHeredityMigraine((prev) => {
                      const isChecked = prev.includes(value);

                      if (value === 'nej') {
                        return isChecked ? [] : ['nej'];
                      }

                      const filtered = prev.filter((item) => item !== 'nej');

                      if (isChecked) {
                        return filtered.filter((item) => item !== value);
                      }
                      return [...filtered, value];
                    });
                  }}
                  style={{ marginRight: '8px' }}
                />
                <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                  {relative}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box id="q-30" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Finns någon annan neurologisk sjukdom i släkten?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="neurologicalFamilyDiseases"
                value="yes"
                checked={neurologicalFamilyDiseases === 'yes'}
                onChange={() => setNeurologicalFamilyDiseases('yes')}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Ja
              </Typography>
            </Box>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="neurologicalFamilyDiseases"
                value="no"
                checked={neurologicalFamilyDiseases === 'no'}
                onChange={() => {
                  setNeurologicalFamilyDiseases('no');
                  setNeurologicalFamilyDiagnosis('');
                  setNeurologicalFamilyRelation('');
                }}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Nej
              </Typography>
            </Box>
          </Box>
          {neurologicalFamilyDiseases === 'yes' && (
            <Box sx={{ mt: 1 }}>
              <input
                type="text"
                onBlur={handleAutoSave}
                placeholder="Vilken diagnos?"
                value={neurologicalFamilyDiagnosis || ''}
                onChange={(e) => setNeurologicalFamilyDiagnosis(e.target.value)}
                style={inputStyle}
              />
              <input
                type="text"
                onBlur={handleAutoSave}
                placeholder="Hos vem (faster, syskon, förälder etc)?"
                value={neurologicalFamilyRelation || ''}
                onChange={(e) => setNeurologicalFamilyRelation(e.target.value)}
                style={inputStyle}
              />
            </Box>
          )}
        </Box>

        <Box id="q-31" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Har du några andra sjukdomar?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="otherConditions"
                value="yes"
                checked={otherConditions === 'yes'}
                onChange={() => setOtherConditions('yes')}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Ja
              </Typography>
            </Box>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="otherConditions"
                value="no"
                checked={otherConditions === 'no'}
                onChange={() => {
                  setOtherConditions('no');
                  setOtherConditionDetails('');
                }}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Nej
              </Typography>
            </Box>
          </Box>
          {otherConditions === 'yes' && (
            <input
              type="text"
              onBlur={handleAutoSave}
              placeholder="Diagnos och debutår"
              value={otherConditionDetails || ''}
              onChange={(e) => setOtherConditionDetails(e.target.value)}
              style={inputStyle}
            />
          )}
        </Box>

        <Box id="q-32" sx={{ mb: 3 }}>
          <FormRadioGroup
            label="Har du en känd hypertoni?"
            name="hypertension"
            value={hypertension}
            options={[
              { label: 'Ja', value: 'yes' },
              { label: 'Nej', value: 'no' },
            ]}
            onChange={(val) => setHypertension(val)}
            onBlur={handleAutoSave}
          />
        </Box>

        <Box id="q-33" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Har du mätt ditt blodtryck någon gång senaste året?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="bloodPressureLastYear"
                value="yes"
                checked={bloodPressureLastYear === 'yes'}
                onChange={() => setBloodPressureLastYear('yes')}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Ja
              </Typography>
            </Box>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="radio"
                onBlur={handleAutoSave}
                name="bloodPressureLastYear"
                value="no"
                checked={bloodPressureLastYear === 'no'}
                onChange={() => setBloodPressureLastYear('no')}
                style={{ marginRight: '8px' }}
              />
              <Typography sx={{ fontSize: '14px', fontFamily: DS.fontBody }}>
                Nej
              </Typography>
            </Box>
          </Box>
          {bloodPressureLastYear === 'yes' && (
            <input
              type="text"
              onBlur={handleAutoSave}
              placeholder="Vad var blodtrycket när du senast kontrollerade det?"
              value={bloodPressureValue || ''}
              onChange={(e) => setBloodPressureValue(e.target.value)}
              style={inputStyle}
            />
          )}
        </Box>

        <Box id="q-34" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Ange din vikt och längd:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <input
              type="text"
              onBlur={handleAutoSave}
              placeholder="Vikt (kg)"
              value={weight || ''}
              onChange={(e) => setWeight(e.target.value)}
              style={{ ...inputStyle, width: '50%', marginTop: 0 }}
            />
            <Typography
              sx={{ pt: 1, fontFamily: DS.fontBody, fontSize: '16px' }}
            >
              (kg)
            </Typography>
            <input
              type="text"
              onBlur={handleAutoSave}
              placeholder="Längd (cm)"
              value={height || ''}
              onChange={(e) => setHeight(e.target.value)}
              style={{ ...inputStyle, width: '50%', marginTop: 0 }}
            />
            <Typography
              sx={{ pt: 1, fontFamily: DS.fontBody, fontSize: '16px' }}
            >
              (cm)
            </Typography>
          </Box>
        </Box>

        <Box id="q-35" sx={{ mb: 3 }}>
          <FormRadioGroup
            label="Uppge biologiskt kön:"
            name="biologicalSex"
            value={biologicalSex}
            options={[
              { label: 'Kvinna', value: 'female' },
              { label: 'Man', value: 'male' },
            ]}
            onChange={(val) => {
              setBiologicalSex(val);
              if (val !== 'female') {
                setPregnant('');
                setPregnancyWeek('');
                setPregnancyPlan('');
                setContraceptiveUse('');
                setContraceptiveType('');
                setBreastfeeding('');
              }
            }}
            onBlur={handleAutoSave}
          />
        </Box>

        {biologicalSex === 'female' && (
          <Box sx={{ mb: 3 }}>
            <FormRadioGroup
              label="Är du gravid?"
              name="pregnant"
              value={pregnant}
              options={[
                { label: 'Ja', value: 'yes' },
                { label: 'Nej', value: 'no' },
                { label: 'Vet inte', value: 'unsure' },
              ]}
              onChange={(val) => {
                setPregnant(val);
                if (val !== 'yes') setPregnancyWeek('');
                if (val !== 'no') {
                  setPregnancyPlan('');
                  setContraceptiveUse('');
                  setContraceptiveType('');
                }
              }}
              onBlur={handleAutoSave}
            />

            {pregnant === 'yes' && (
              <Box sx={{ mt: 2 }}>
                <FormTextInput
                  label="Ungefär vilken graviditetsvecka är du i?"
                  type="number"
                  min={1}
                  max={44}
                  value={pregnancyWeek}
                  onChange={(e) => {
                    const input = e.target;
                    if (input.validity.badInput) {
                      input.value = pregnancyWeek;
                      return;
                    }
                    if (isValidPregnancyWeek(input.value)) {
                      setPregnancyWeek(input.value);
                    }
                  }}
                  onBlur={handleAutoSave}
                />
              </Box>
            )}

            {pregnant === 'no' && (
              <Box sx={{ mt: 2 }}>
                <FormRadioGroup
                  label="Planerar du graviditet inom närmaste 6 månaderna?"
                  name="pregnancyPlan"
                  value={pregnancyPlan}
                  options={[
                    { label: 'Ja', value: 'yes' },
                    { label: 'Nej', value: 'no' },
                  ]}
                  onChange={(val) => setPregnancyPlan(val)}
                  onBlur={handleAutoSave}
                />
              </Box>
            )}

            {pregnancyPlan && (
              <Box sx={{ mt: 2 }}>
                <FormRadioGroup
                  label="Använder du preventivmedel?"
                  name="contraceptiveUse"
                  value={contraceptiveUse}
                  options={[
                    { label: 'Ja', value: 'yes' },
                    { label: 'Nej', value: 'no' },
                  ]}
                  onChange={(val) => setContraceptiveUse(val)}
                  onBlur={handleAutoSave}
                />

                {contraceptiveUse === 'yes' && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: '#374151',
                        mb: 1,
                        fontFamily: DS.fontBody,
                        fontSize: '16px',
                      }}
                    >
                      Vilket preventivmedel?
                    </Typography>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                    >
                      {[
                        'hormonspiral',
                        'kopparspiral',
                        'p-piller',
                        'p-stav',
                        'p-spruta',
                        'kondom',
                        'annat',
                      ].map((type) => (
                        <Box
                          component="label"
                          key={type}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="radio"
                            onBlur={handleAutoSave}
                            name="contraceptiveType"
                            value={type}
                            checked={contraceptiveType === type}
                            onChange={() => setContraceptiveType(type)}
                            style={{ marginRight: '8px' }}
                          />
                          <Typography
                            sx={{ fontSize: '14px', fontFamily: DS.fontBody }}
                          >
                            {type}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <FormRadioGroup
                label="Ammar du?"
                name="breastfeeding"
                value={breastfeeding}
                options={[
                  { label: 'Ja', value: 'yes' },
                  { label: 'Nej', value: 'no' },
                ]}
                onChange={(val) => setBreastfeeding(val)}
                onBlur={handleAutoSave}
              />
            </Box>
          </Box>
        )}

        <Box id="q-37" sx={{ mb: 3 }}>
          <FormRadioGroup
            label="Efter att ha svarat på ovanstående har du kanske någon ytterligare viktig information som du vill lägga till och/eller några specifika önskemål eller förväntningar på besöket?"
            name="additionalInfo"
            value={additionalInfo}
            options={[
              { label: 'Ja', value: 'yes' },
              { label: 'Nej', value: 'no' },
            ]}
            onChange={(val) => setAdditionalInfo(val)}
            onBlur={handleAutoSave}
          />
        </Box>

        {additionalInfo === 'yes' && (
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Uppge viktig information som du vill lägga till och/eller om du
              har några specifika önskemål eller förväntningar på besöket.
            </Typography>
            <textarea
              placeholder="Uppge viktig information eller specifika önskemål (max 25 ord)"
              value={additionalInfoText || ''}
              onBlur={handleAutoSave}
              onChange={(e) => setAdditionalInfoText(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontFamily: DS.fontBody,
                fontSize: '16px',
                boxSizing: 'border-box',
              }}
              maxLength={250}
            />
          </Box>
        )}
        <>
          {showUnanswered && (
            <UnansweredQuestions ref={incompleteRef} unanswered={unanswered} />
          )}

          <Box
            role="group"
            aria-labelledby="confirm-submit-title"
            sx={{
              mx: -3,
              borderTop: '1px solid',
              borderBottom: '1px solid',
              borderColor: '#fecdd3',
              backgroundColor: '#fff1f2',
              pl: 3,
              pr: 4,
              py: 1.5,
              color: '#111827',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                id="confirmSubmit"
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
                style={{
                  marginTop: '4px',
                  width: '16px',
                  height: '16px',
                  accentColor: DS.deepTeal,
                }}
              />
              <Typography
                component="label"
                htmlFor="confirmSubmit"
                sx={{
                  ml: 1.5,
                  fontSize: '16px',
                  lineHeight: 1.6,
                  fontFamily: DS.fontBody,
                }}
              >
                Jag har besvarat alla frågor och är medveten om att jag inte kan
                ändra eller lägga till något när jag skickat in.
              </Typography>
            </Box>
          </Box>

          <Button
            type="submit"
            name="SaveForm"
            disabled={submitDisabled}
            aria-describedby={
              showUnanswered ? UNANSWERED_QUESTIONS_ID : undefined
            }
            sx={{
              mt: 3,
              width: '100%',
              px: 3,
              py: 2,
              fontSize: '16px',
              fontWeight: 400,
              fontFamily: DS.fontBody,
              borderRadius: '4px',
              backgroundColor: submitDisabled ? '#D1D5DB' : DS.deepTeal,
              color: '#fff',
              textTransform: 'uppercase',
              '&:hover': {
                backgroundColor: submitDisabled ? '#D1D5DB' : '#0a5854',
              },
              '&:disabled': { backgroundColor: '#D1D5DB', color: '#fff' },
            }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ color: '#fff', mr: 1 }} />
                SKICKAR IN...
              </>
            ) : (
              'SKICKA IN FORMULÄR'
            )}
          </Button>

          {error && (
            <Typography
              ref={errorRef}
              sx={{
                mt: 1,
                color: 'error.main',
                fontSize: '14px',
                fontFamily: DS.fontBody,
              }}
            >
              {error}
            </Typography>
          )}
        </>
      </FormLayout>
    </form>
  );
};

export default NewVisitForm;
